import { debug } from "console";
import { Action, Reducer } from "redux";


// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export interface PatientState {
    facilityId?: number;
    refMdId?: number;
    practiceId?: number;
    patientsByRefMDId: ListPatient[];
    patientsByPracticeId: ListPatient[];
    fullPatients: Patient[];
    isLoading: boolean;
    isLoaded: boolean;
    isError: boolean;
    errorMessage: string;
}

export interface FocusPatientState {
    isLoading: boolean;
    patient?: Patient;
    patientId: number;
}

export interface FocusMedicalRecordState {
    isLoading: boolean;
    isLoaded: boolean;
    isErrored: boolean,
    medicalRecord?: PatientMedicalRecord;
    patientId: number;
    recordName: string;
}

export interface FocusMessageState {
    isLoading: boolean;
    message?: Message;
    messageId: number;
    isErrored: boolean;
    errorMessage: string;
}

export interface SpecialtyState {
    isLoading: boolean;
    isLoaded: boolean,
    VitalsReferenceSpecialties: VitalsReferenceSpecialties;
}

export interface MessageState {
    isLoading: boolean;
    isLoaded: boolean,
    failedLoading: boolean,
    CurrentMessages: Message[];
    FocusMessage: FocusMessageState;
    Errors: MessageStateError[];
    SendMessageState: SendMessageState;
    ReplyOptions: string[];
}



export interface NotificationCountState {
    isLoading: boolean;
    isLoaded: boolean;
    isErrored: boolean;
    UnreadCount: number
}

export interface NotificationCountDto {
    unreadCount: number;
    isErrored: boolean;
    UnreadCount: number;
}

export interface MessageStateError {
    messageId: number,
    error: string
}

export interface SendMessageState {
    message: Message,
    isSent: boolean,
    isErrored: boolean,
    error: string;
}

export interface AdmitSummaryState {
    physicianAdmits: AdmitSummaryStatistics[];
    isErrored: boolean,
    error: string,
    isLoading: boolean,
    isLoaded: boolean
}

export interface DischargeLocationState {
    dischargeLocations: DischargeLocationStatistics[];
    isErrored: boolean,
    error: string,
    isLoading: boolean
}

export interface DischargeDispositionsState {
    dischargeDispositions: DischargeDispositionStatistics[];
    isErrored: boolean,
    error: string,
    isLoading: boolean;
    isLoaded: boolean;
}

export interface PatientLocationStatsState {
    patientFacilityCounts: PatientFacility[];
    isErrored: boolean,
    error: string,
    isLoading: boolean,
    isLoaded: boolean
}

export interface PatientFacilityDrillDownState {
    patientFacilityDrillDownCounts: FacilityLocationStats | null;
    isErrored: boolean,
    error: string,
    isLoading: boolean,
    isLoaded: boolean
}

export interface PracticeState {
    isErrored: boolean,
    error: string,
    isLoading: boolean,
    isLoaded: boolean,
    practice: Practice
}

export interface PracticeCoordinatorState {
    isErrored: boolean,
    error: string,
    isLoading: boolean,
    isLoaded: boolean,
    notFound: boolean,
    coordinatorProfile: CareCoordinatorModel
}

export interface RefMDProfileState {
    isErrored: boolean,
    notFound: boolean,
    error: string,
    isLoading: boolean,
    isLoaded: boolean,
    refmdProfile?: RefMdProfile
}

export interface PhysicianContactsState {
    isErrored: boolean,
    error: string,
    isLoading: boolean,
    isLoaded: boolean,
    userPatientContacts: HNIProviderContact[];
}

export interface HNIContextState {
    PhysicianAdmitsStats: any;
    PhysicianAdmitMonthStats: any;
    DischargeLocationStats: any;
    isLoading: boolean;
    isDemoMode: boolean;
    apiHost: string;
    startDateIndex?: number;
    userRefMdProfile: RefMDProfileState,
    userPracticeCoordinatorProfile: PracticeCoordinatorState;
    practice: PracticeState;
    Patients: PatientState;
    MedicalRecord: FocusMedicalRecordState;
    FocusPatient: FocusPatientState;
    SystemSpecialties: SpecialtyState;
    Messages: MessageState;
    NotificationCount: NotificationCountState;
    PatientPhysiciansContacts: PhysicianContactsState;
    admitStats: AdmitSummaryState;
    DischargeDispositionStats: DischargeDispositionsState;
    PatientLocationStats: PatientLocationStatsState;
    PatientFacilityDrillDownStats: PatientFacilityDrillDownState;
    AllMessageTypes: MessageType[];
    APIToken: TokenType;
    PatientListParams: PatientListParams,
    MessageParams: NotificationParams;
    PhysicianTypeStats: AdmitTypeStatsState;
}

export interface PatientListParams {
    daysBack: number,
    columnFilterMap: Map<string, Array<string>>,
}

export interface NotificationParams {
    daysBack: number
}

export interface HNIProviderContact {
    displayName: string;
    type: string;
    email: string;
    lastName: string;
}

// -----------------
// ACTIONS - These are serializable (hence replayable) descriptions of state transitions.
// They do not themselves have any side-effects; they just describe something that is going to happen.

export interface ListPatient {
    firstName: string;
    lastName: string;
    facilityName: string;
    ptId: number;
    facilityId: number;
    mrn: string;
    mbi: string,
    pcn: string;
    dateOfBirth: string;
    primaryProviderContact: HNIProviderContact,
    referringMdContact: HNIProviderContact,
    admitDate: string;
    admitStamp: number;
    dischargeDate: string;
    daysToDischarge: number;
    roomNumber: string;
    recentAdmits: number;
    referringMdUserName: string;
    primaryCareProvider: string;
    payer: string;
    dischargeDisposition: string;
}

export interface Patient {
    firstName: string;
    lastName: string;
    patientEncounterId: number;
    facilityId: number,
    facilityName: string,
    mrn: string;
    policyNumber: string,
    pcn: string;
    dateOfBirth: string,
    primaryProviderContact: HNIProviderContact,
    dateOfAdmission: string,
    dateOfDischarge: string,
    encounters: Encounter[],
    mostRecentEncounter: Encounter,
    insuranceName: string,
    historicalDocuments: DownloadablePatientDocument[],
    historicalMedicalRecords: PatientMedicalRecord[],
}

export interface Practice {
    practiceId: number;
    practiceName: string;
    tagDescription: string;
    isRosterPractice: boolean
}

export interface CareCoordinatorModel {
    azureAdAccount: string;
    practiceCareCoordinatorId: number;
    firstName: string;
    lastName: string;
    cell: string;
    phone: string;
    emailPreferred: boolean | undefined,
    textPreferred: boolean | undefined,
    notifyAdmits: boolean,
    notifyDischarges: boolean,
    defaultPractice: Practice,
    practices: Practice[]
}

export interface Message {
    detail: any;
    subject: string,
    senderId: string,
    description: string,
    receiverId: string,
    sendDate: string,
    createdDate: string,
    messageId: number,
    message: string,
    parentMessageId: number,
    status: string,
    ptId: string,
    patientLastName: string,
    patientFirstName: string,
    document: Blob | undefined,
    documentFileName: string,
    messageReadDate: string,
    createdStamp: number,
    practiceName: string
}

export interface MessageType {
    messageTypeId: number;
    description: string;
}

export interface Encounter {
    encounterId: number,
    patientId: number,
    facility: VitalsFacility,
    encounterStart: string,
    encounterEnd: string,
    dateOfBirth: string,
    roomNumber: string,
    primaryProviderContact: HNIProviderContact,
    dischargingPhysician: HNIProviderContact,
    pcpFromRosterContact: HNIProviderContact,
    daysUntilDischarge: number,
    mrn: string,
    pcn : string,
    patientDiagnosisList: PatientDiagnosis[],
    patientChargeList: PatientCharge[],
    encounterInsurance: VitalsInsurance,
    downloadablePatientDocuments: DownloadablePatientDocument[],
    patientMedicalRecords: PatientMedicalRecord[],
    nextSiteOfCare: NextSiteOfCare,
    referringMD: HNIProviderContact
}

export interface DownloadablePatientDocument {
    name: string,
    path: string,
    createdBy: string,
    displayName: string,
    createdDate: string,
    extension: string,
    id: string
}

export interface PatientMedicalRecord {
    displayName: string,
    lastName: string,
    firstName: string,
    condensedName: string,
    ptId: number,
    recordName: string,
    documentType: string,
    content: string,
    contentType: string,
    summary: string,
    recordType: string,
    createdBy: string,
    createdDate: string,
    modifiedBy: string,
    modifiedDate: string,
}

export interface PatientDiagnosis {
    diagnosedBy: string,
    diagnosis: Diagnosis,
    shortDescription: string,

}

export interface PatientCharge {
    ChargeAddedBy: string,
    procedureModel: Procedure
}

export interface Diagnosis {
    diagnosisCodeValue: string,
    codingSystem: string,
    dxId: number,
    isRiskAdjusted: boolean,
    hccCategory: string,

}

export interface Procedure {
    procedureCodeValue: string
}

export interface VitalsInsurance {
    companyNameFromFacility: string
}

export interface DocumentResponseModel {
    documentId: string
}


export interface VitalsFacility {
    facilityName: string,
    facilityId: number,
    facilityProgramManager: VitalsProgramManager
}

export interface VitalsProgramManager {
    email: string,
    name: string
}

export interface RefMdProfile {
    referringMDId: number,
    firstName: string,
    lastName: string,
    email: string,
    npi: string,
    phone: string,
    specialties: VitalsReferenceSpecialty[],
    notifyAdmits: boolean,
    notifyDischarges: boolean,
    emailPreferred: boolean,
    textPreferred: boolean,
}

export interface AdmitSummaryStatistics {
    referringMDId: string,
    assignedMDUserName: string,
    year: number,
    totalAdmits: number,
    month: number,
    day: number,
    practiceId: number
}

export interface AdmitTypeStatistics {
    admitType: string,
    totalAdmits: number,
    month: number,
    year: number,
    practiceId: number
}

export interface FacilityLocationStats {
    admitsByMonth: AdmitSummaryStatistics[];
    admitsByType: AdmitTypeStatistics[];
}

export interface AdmitTypeStatsState {
    admitTypeStats: AdmitTypeStatistics[];
    isErrored: boolean,
    error: string,
    isLoading: boolean,
    isLoaded: boolean
}

export interface DischargeLocationStatistics {
    dischargeFacilityName: string,
    dischargeCount: number
}

export interface DischargeDispositionStatistics {
    typeDesciption: string,
    patientCount: number
}

export interface PatientFacility {
    facilityName: string,
    facilityId: number,
    patientsCount: number,
}

export interface VitalsReferenceSpecialty {
    specialtyId: string,
    description: string
}

export interface VitalsReferenceSpecialties {
    AsOfDate: Date,
    SystemSpecialties: VitalsReferenceSpecialty[]
}

export interface Profile {
    name: string,
    unique_name: string,
    family_name: string,
    upn: string
}

export interface TokenType {
    accessToken: string,
}

export interface PatientListRequest {
    RefMDId: number;
    MonthsBack: number;
    IncludeInactivePatients: boolean;
}

export interface MedicalRecordRequest {
    PtId: number;
    DocumentName: string;
}


export interface NextSiteOfCare {
    ccn: string,
    name: string
}

interface CareCoordinatorRequest {
    PracticeId: number;
    PracticeCareCoordinatorId: number;
    AzureADUserName: string | undefined;
    DaysBack: number;
    StartDate?: string | null;
}

interface UpdatePatientListParams {
    type: "UPDATE_PATIENT_LIST_PARAMS";
    patientListParams: PatientListParams;
}

interface UpdateMessageParams {
    type: "UPDATE_MESSAGE_PARAMS";
    messageParams: NotificationParams;
}

interface RequestPatientsByRefMDIdAction {
    type: "REQUEST_PATIENTS_BY_REFMD_ID";
    refMdId: number;
}

interface RequestPatientByIdAction {
    type: "Patients";
    patientId: number;
}

interface RequestPatientByPracticeId {
    type: "REQUEST_PATIENTS_BY_PRACTICE_ID";
    practiceId: number;
}


interface RequestUserRefMdProfile {
    type: "REQUEST_USER_REFMD_PROFILE";
}

interface RequestAdmitTypeStats {
    type: "REQUEST_ADMIT_TYPE_STATS";
}

interface ReceiveAdmitTypeStats {
    type: "RECEIVE_ADMIT_TYPE_STATS";
    admitTypeStats: AdmitTypeStatistics[];
}

interface ReceiveAdmitTypeStatsError {
    type: "REQUEST_ADMIT_TYPE_STATS_ERROR";
    errMessage: string;
}

interface RequestAllAvailableSpecialties {
    type: "REQUEST_AVAILABLE_SPECIALTIES";
}

interface RequestCurrentMessages {
    type: "REQUEST_CURRENT_MESSAGES";
}

interface RequestUpdateRefMDProfile {
    type: "UPDATE_REF_MD_PROFILE";
    profile: RefMdProfile;
}

interface RequestUserCareCoordinatorProfile {
    type: "REQUEST_USER_CARE_COORD_PROFILE";
}

interface RequestRefMDAdmitStats {
    type: "REQUEST_ADMIT_STATS";
}

interface RequestRefMDDischargeLocationStats {
    type: "REQUEST_DISCHARGE_STATS_LOCATIONS";
}

interface RequestRefMDPatientLocationStats {
    type: "REQUEST_PATIENT_STATS_LOCATIONS";
}

interface RequestNSOCTypeStats {
    type: "REQUEST_NSOC_TYPE_STATS";
}

interface ReceivePatientsByRefMDIdAction {
    type: "RECEIVE_PATIENTS_BY_REFMD_ID";
    refMdId: number;
    patientsByRefMDId: ListPatient[];
}

interface ReceivePatientsByRefMDIdError {
    type: "RECEIVE_PATIENTS_BY_REFMD_ID_ERROR";
    err: string
}

interface ReceivePatientsByPractice {
    type: "RECEIVE_PATIENTS_BY_PRACTICEID";
    practiceId: number;
    patientsByPracticeId: ListPatient[];
}

interface ReceivePatientByIdAction {
    type: "RECEIVE_PATIENT_BY_ID";
    patientId: number;
    patient: Patient;
}

interface ReceivetUserRefMdProfile {
    type: "RECEIVE_USER_REFMD_PROFILE";
    profile: RefMdProfile;
}

interface ReceiveUserRefMDProfileError {
    type: "RECEIVE_USER_REFMD_PROFILE_FAILED";
}


interface UserRefMdProfileNotFound {
    type: "RECEIVE_USER_REFMD_PROFILE_NOT_FOUND";
}

interface ReceiveUserCareCoordinatorProfile {
    type: "RECEIVE_USER_CARE_COORD_PROFILE";
    coordinatorProfile: CareCoordinatorModel;
}

interface ReceiveUserCareCoordinatorProfileNotFound {
    type: "RECEIVE_USER_CARE_COORD_PROFILE_NOT_FOUND";
}

interface ReceiveAllAvailableSpecialties {
    type: "RECEIVE_ALL_SYSTEM_SPECIALTIES";
    AvailableSpecialties: VitalsReferenceSpecialties;
}

interface ReceiveCurrentMessages {
    type: "RECEIVE_CURRENT_MESSAGES";
    Messages: Message[];
}

interface ReceiveFocusMessage {
    type: "RECEIVED_FOCUS_MESSAGE";
    Message: Message;
}

interface ReceiveFocusMessageError {
    type: "RECEIVE_FOCUS_MESSAGE_ERROR";
    errorMessage: string
}

interface RequestFetchFocusMessage {
    type: "REQUEST_FOCUS_MESSAGE";
}

interface RequestFetchFocusMessagePartial {
    type: "REQUEST_FOCUS_MESSAGE_PARTIAL";
}

interface ReceiveCurrentMessagesError {
    type: "RECEIVE_CURRENT_MESSAGES_ERROR";
    ErrorMessage: string;
}

interface ReceiveMessageSendResponse {
    type: "RECEIVE_SENT_MESSAGE";
    Messages: Message;
}


interface SubmitMessageReply {
    type: "SUBMIT_MESSAGE_REPLY";
}

interface SubmitNewMessage {
    type: "SUBMIT_NEW_MESSAGE";
}

interface ReceivedMessageReplyResponse {
    type: "RECEIVED_MESSAGE_REPLY_RESPONSE";
    message: Message;
}

interface ReceivedNewMessageResponse {
    type: "RECEIVED_NEW_MESSAGE_SEND_RESPONSE";
    message: Message;
}

interface ReceivedNewMessageSendError {
    type: "RECEIVED_NEW_MESSAGE_SEND_RESPONSE_ERROR";
    ErrorMessage: string;
}

interface ArchiveMessageResponse {
    type: "ARCHIVED_MESSAGE_RESPONSE";
    archived: Message;
}

interface ArchiveMessageRequest {
    type: "ARCHIVE_MESSAGE_REQUEST";
}

interface ArchiveMessagesResponse {
    type: "ARCHIVED_MESSAGES_RESPONSE";
    archived: Message[];
}

interface ArchiveMessageResponseError {
    type: "ARCHIVE_MESSAGE_ERROR";
    messageId: number;
    errMessage: string;
}

interface ClearSendMessageResponse {
    type: "CLEAR_SEND_MESSAGE_RESPONSE"
}

interface ReceiveRefMdAdmitStats {
    type: "RECEIVE_REF_MD_ADMIT_STATS",
    admitStats: AdmitSummaryStatistics[]
}

interface ReceiveAdmitStatusError {
    type: "REQUEST_ADMIT_STATS_ERROR";
    errMessage: string;
}

interface RequestAdmitMonthStats {
    type: "REQUEST_ADMIT_MONTH_STATS";
}

interface ReceiveAdmitMonthStats {
    type: "RECEIVE_ADMIT_MONTH_STATS",
    admitStats: AdmitSummaryStatistics[]
}

interface ReceiveAdmitMonthStatsError {
    type: "REQUEST_ADMIT_MONTH_STATS_ERROR";
    errMessage: string;
}

interface ReceiveRefMdDischargeLocationStats {
    dischargeLocations: any;
    type: 'RECEIVE_DISCHARGE_STATS_LOCATIONS',
    dischageLocations: DischargeLocationStatistics[]
}

interface ReceiveNSOCTypeStats {
    type: 'RECEIVE_NSOC_STATS',
    dischargeDispositions: DischargeDispositionStatistics[]
}


interface RequestPatientLocationStats {
    type: 'REQUEST_PATIENTS_LOCATION_STATS'
}

interface ReceivePatientLocationStats {
    type: 'RECEIVE_PATIENTS_LOCATION_STATS',
    patientFacilityCounts: PatientFacility[]
}

interface ReceivePatientLocationStatsError {
    type: 'RECEIVE_PATIENTS_LOCATION_STATS_ERROR',
    errMessage: string;
}

interface RequestPatientLocationDrillDownStats {
    type: 'REQUEST_PATIENT_LOCATION_DRILLDOWN_STATS';
}

interface ReceivePatientLocationDrillDownStats {
    type: 'RECEIVE_PATIENT_LOCATION_DRILLDOWN_STATS';
    patientFacilityDrillDownCounts: FacilityLocationStats;
}

interface ReceivePatientLocationDrillDownStatsError {
    type: 'RECEIVE_PATIENT_LOCATION_DRILLDOWN_STATS_ERROR';
    errMessage: string;
}

interface ReceiveDischargeStatsLocationsError {
    type: 'RECEIVE_DISCHARGE_STATS_LOCATIONS_ERROR';
    errMessage: string;
}

interface ReceiveDischargeNSOCTypeError {
    type: 'RECEIVE_NSOC_STATS_ERROR';
    errMessage: string;
}

interface ReceiveUpdatedApiHost {
    type: 'UPDATE_API_HOST';
    apiHost: string;
}

interface RequestNotificationUnreadCount {
    type: 'REQUEST_NOTIFICATION_UNREAD_COUNT';
}

interface ReceiveNotificationUnreadCountError {
    type: 'RECEIVE_NOTIFICATION_UNREAD_COUNT_ERROR'
}

interface ReceiveNotificationUnreadCount {
    type: 'RECEIVE_NOTIFICATION_UNREAD_COUNT';
    countStatus: NotificationCountDto;
}

interface UpdateCareCoordinatorProfile {
    type: 'UPDATE_CARE_COORD_PROFILE',
    coordinatorProfile: CareCoordinatorModel
}

interface RequestUserPracticePhysicianContacts {
    type: 'REQUEST_USER_PRACTICE_PHYSICIAN_CONTACTS';
}

interface ReceiveUserPracticePhysicianContacts {
    type: 'RECEIVE_USER_PRACTICE_PHYSICIAN_CONTACTS';
    physicianContacts: HNIProviderContact[];
}

interface ReceiveTokenResponse {
    type: 'RECEIVE_TOKEN_RESPONSE';
    apiToken: TokenType;
}

interface RequestPatientMedicalRecord {
    type: "REQUEST_PATIENT_MEDICAL_RECORD_NAME";
    ptid: number;
    name: string;
}

interface ReceivePatientMedicalRecord {
    type: 'RECEIVE_PATIENT_MEDICAL_RECORD_NAME';
    subjectMedicalRecord: PatientMedicalRecord;
}



// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
    | RequestPatientsByRefMDIdAction
    | ReceivePatientsByRefMDIdAction
    | RequestPatientByIdAction
    | ReceivePatientByIdAction
    | RequestUserRefMdProfile
    | ReceivetUserRefMdProfile
    | RequestAllAvailableSpecialties
    | ReceiveAllAvailableSpecialties
    | RequestUpdateRefMDProfile
    | RequestCurrentMessages
    | ReceiveCurrentMessages
    | SubmitMessageReply
    | ReceivedMessageReplyResponse
    | ArchiveMessageRequest
    | ArchiveMessageResponse
    | ArchiveMessagesResponse
    | ArchiveMessageResponseError
    | ReceiveMessageSendResponse
    | SubmitNewMessage
    | ReceivedNewMessageResponse
    | ClearSendMessageResponse
    | ReceiveCurrentMessagesError
    | ReceivedNewMessageSendError
    | ReceiveRefMdAdmitStats
    | RequestRefMDAdmitStats
    | RequestRefMDDischargeLocationStats
    | ReceiveRefMdDischargeLocationStats
    | ReceiveDischargeStatsLocationsError
    | ReceiveAdmitStatusError
    | RequestAdmitMonthStats
    | ReceiveAdmitMonthStats
    | ReceiveAdmitMonthStatsError
    | ReceiveDischargeNSOCTypeError
    | RequestNSOCTypeStats
    | ReceiveNSOCTypeStats
    | ReceivePatientLocationStats
    | RequestRefMDPatientLocationStats
    | ReceivePatientLocationStatsError
    | ReceiveUpdatedApiHost
    | ReceivePatientsByRefMDIdError
    | ReceiveFocusMessage
    | ReceiveFocusMessageError
    | RequestFetchFocusMessage
    | RequestNotificationUnreadCount
    | ReceiveNotificationUnreadCount
    | ReceiveNotificationUnreadCountError
    | RequestPatientByPracticeId
    | ReceivePatientsByPractice
    | RequestUserCareCoordinatorProfile
    | ReceiveUserCareCoordinatorProfile
    | UserRefMdProfileNotFound
    | UpdateCareCoordinatorProfile
    | ReceiveUserRefMDProfileError
    | RequestPatientLocationStats
    | ReceiveUserCareCoordinatorProfileNotFound
    | RequestUserPracticePhysicianContacts
    | ReceiveUserPracticePhysicianContacts
    | RequestFetchFocusMessagePartial
    | RequestPatientLocationDrillDownStats
    | ReceivePatientLocationDrillDownStats
    | ReceivePatientLocationDrillDownStatsError
    | ReceiveTokenResponse
    | RequestPatientMedicalRecord
    | ReceivePatientMedicalRecord
    | UpdatePatientListParams
    | UpdateMessageParams
    | RequestAdmitTypeStats
    | ReceiveAdmitTypeStats
    | ReceiveAdmitTypeStatsError


// ----------------
// ACTION CREATORS - These are functions exposed to UI components that will trigger a state transition.
// They don't directly mutate state, but they can have external side-effects (such as loading data).

let host = process.env.REACT_APP_API_ENDPOINT;

export const findMessage = (messageId: number, messages: Message[]) => {
    let ret = messages.filter((val) => val.messageId === messageId);
    if (!ret.length) {
        return null;
    }

    return ret[0];
};

const addMessageIfMissing = (newMessage: Message, messages: Message[]) => {
    let exMessage = findMessage(newMessage.messageId, messages);
    if (!exMessage) {
        messages = messages.concat(newMessage);
        return messages;
    }

    return messages;
};

const filterServiceDeskContacts = (source: HNIProviderContact[]) => {
    return source.filter(v => v.email != 'support@hnihc.com')
}



export const actionCreators = {

    submitRefMD: (profileState: any, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState && appState.vitalsContext?.userRefMdProfile) {
            const options = {
                headers: {
                    Accept: "application/json",
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,

                },
                method: "POST",
                body: JSON.stringify(profileState),

            };
            fetch(`https://${host}/ReferringMD/profile/`, options)
                .then((response) => response.json() as Promise<RefMdProfile>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_USER_REFMD_PROFILE",
                        profile: profileState
                    });
                })
                .catch((err) => {
                    console.log(err);
                });

            dispatch({
                type: "UPDATE_REF_MD_PROFILE",
                profile: profileState
            });
        }
    },

    submitCareCoordinator: (profileState: CareCoordinatorModel, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState && appState.vitalsContext?.userPracticeCoordinatorProfile) {
            const options = {
                headers: {
                    Accept: "application/json",
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,

                },
                method: "POST",
                body: JSON.stringify(profileState),
            };

            fetch(`https://${host}/CareCoordinator/profile/`, options)
                .then((response) => response.json() as Promise<CareCoordinatorModel>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_USER_CARE_COORD_PROFILE",
                        coordinatorProfile: data
                    });
                })
                .catch((err) => {
                    console.log(err);
                });

            dispatch({
                type: "UPDATE_CARE_COORD_PROFILE",
                coordinatorProfile: profileState
            });
        }
    },

    clearSentMessageState: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({
            type: "CLEAR_SEND_MESSAGE_RESPONSE"
        });
    },

    receiveTokenResponse: (apiToken: TokenType): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({
            type: "RECEIVE_TOKEN_RESPONSE",
            apiToken: apiToken
        });
    },

    getUnreadMessageCount: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_NOTIFICATION_UNREAD_COUNT",
                countStatus: unloadedState.NotificationCount
            });
            return unloadedState.NotificationCount;
        }

        let messageParms: NotificationParams = {
            daysBack: appState.vitalsContext?.MessageParams?.daysBack
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode) {
            const options = {
                method: "POST",
                body: JSON.stringify(messageParms),
                headers: {
                    Accept: "application/json",
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,

                },
            }

            //JVH

            fetch(`https://${host}/Message/Messages/UnRead/Days/`, options)

                .then((response): Promise<NotificationCountDto> => {
                    if (!response.ok) {

                        throw Error(response.statusText);
                    }

                    return response.json()
                })
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_NOTIFICATION_UNREAD_COUNT",
                        countStatus: data
                    });
                })
                .catch((err) => {

                    dispatch({
                        type: "RECEIVE_NOTIFICATION_UNREAD_COUNT_ERROR"
                    });
                    console.error(err);
                });

            dispatch({
                type: "REQUEST_NOTIFICATION_UNREAD_COUNT"
            });
        }
    },


    submitReplyMessage: (replyMessage: Message, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();


        let requestMessage = {
            ...replyMessage,
            patient: null
        }
        const options = {
            headers: {
                Accept: "application/json",
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,

            },
            method: "POST",
            body: JSON.stringify(requestMessage),

        };

        fetch(`https://${host}/Message/Messages/Replies/${replyMessage.parentMessageId}/`, options)
            .then((response) => response.json() as Promise<Message>)
            .then((data) => {
                dispatch({
                    type: "RECEIVED_MESSAGE_REPLY_RESPONSE",
                    message: data
                });
            })
            .catch((err) => {
                console.log(err);
            });

        dispatch({
            type: "SUBMIT_MESSAGE_REPLY"
        });
    },


    submitNewMessageWithAttachment: (formData: FormData, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        const options = {
            headers: {
                Accept: "*.*",
                Authorization: `Bearer ${accessToken}`,
            },
            method: "POST",
            body: formData,
        };

        fetch(`https://${host}/Message/Messages/`, options)
            .then((response) => response.json() as Promise<Message>)
            .then((data) => {
                dispatch({
                    type: "RECEIVED_NEW_MESSAGE_SEND_RESPONSE",
                    message: data
                });
            })
            .catch((err) => {
                dispatch({
                    type: 'RECEIVED_NEW_MESSAGE_SEND_RESPONSE_ERROR',
                    ErrorMessage: err.message
                })
            });

        dispatch({
            type: "SUBMIT_NEW_MESSAGE"
        });
    },

    archiveMessage: (subjectMessage: Message, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {

        const appState = getState();

        let requestMessage = {
            ...subjectMessage,
            patient: null
        }

        const options = {
            headers: {
                Accept: "application/json",
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,

            },
            method: "POST",
            body: JSON.stringify(requestMessage),

        };

        fetch(`https://${host}/Message/Messages/Archived/`, options)
            .then((response) => response.json() as Promise<Message>)
            .then((data) => {
                if (data.status == '500') {
                    dispatch({
                        type: "ARCHIVE_MESSAGE_ERROR",
                        messageId: subjectMessage.messageId,
                        errMessage: data.detail
                    });
                    return;
                }
                dispatch({
                    type: "ARCHIVED_MESSAGE_RESPONSE",
                    archived: data
                });
            })
            .catch((err) => {
                dispatch({
                    type: "ARCHIVE_MESSAGE_ERROR",
                    messageId: subjectMessage.messageId,
                    errMessage: err
                });
            });

        dispatch({
            type: "ARCHIVE_MESSAGE_REQUEST"
        });
    },

    archiveMessages: (subjectMessages: Message[]): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        const accessToken = appState.oidc.user.access_token;

        const options = {
            headers: {
                Accept: "application/json",
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,

            },
            method: "POST",
            body: JSON.stringify(subjectMessages),

        };

        fetch(`https://${host}/Message/Messages/Archived/list/`, options)
            .then((response) => response.json() as Promise<Message[]>)
            .then((data) => {
                dispatch({
                    type: "ARCHIVED_MESSAGES_RESPONSE",
                    archived: data
                });
            })
            .catch((err) => {

            });

        dispatch({
            type: "ARCHIVE_MESSAGE_REQUEST"
        });
    },


    requestPatientLocationStatsForProvider: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_PATIENTS_LOCATION_STATS",
                patientFacilityCounts: unloadedState.PatientLocationStats.patientFacilityCounts
            });

            return unloadedState.PatientLocationStats.patientFacilityCounts;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode && !appState.vitalsContext?.PatientLocationStats.isLoaded) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",

                    Authorization: `Bearer ${accessToken}`,
                },
            }

            fetch(`https://${host}/Home/Stats/Locations/`, options)
                .then((response) => response.json() as Promise<PatientFacility[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_LOCATION_STATS",
                        patientFacilityCounts: data

                    });
                })
                .catch((err) => {

                    dispatch({
                        type: "RECEIVE_PATIENTS_LOCATION_STATS_ERROR",
                        errMessage: err.message
                    });

                    console.error(err);
                });

            dispatch({
                type: "REQUEST_PATIENTS_LOCATION_STATS"
            });
        }
    },

    requestPatientLocationDrillDownStatsForProvider: (accessToken: string, facilityId: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_PATIENT_LOCATION_DRILLDOWN_STATS",
                patientFacilityDrillDownCounts: unloadedState.PatientFacilityDrillDownStats.patientFacilityDrillDownCounts
            });

            return unloadedState.PatientFacilityDrillDownStats.patientFacilityDrillDownCounts;        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode && !appState.vitalsContext?.PatientFacilityDrillDownStats.isLoaded) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            }

            fetch(`https://${host}/Home/Stats/Locations/${facilityId}`, options)
                .then((response) => response.json() as Promise<FacilityLocationStats>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENT_LOCATION_DRILLDOWN_STATS",
                        patientFacilityDrillDownCounts: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "RECEIVE_PATIENT_LOCATION_DRILLDOWN_STATS_ERROR",
                        errMessage: err.message
                    });

                    console.error(err);
                });

            dispatch({
                type: "REQUEST_PATIENT_LOCATION_DRILLDOWN_STATS"
            });
        }
    },

    requestPatientLocationStatsByTag: (accessToken: string, practice: Practice): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_PATIENTS_LOCATION_STATS",
                patientFacilityCounts: unloadedState.PatientLocationStats.patientFacilityCounts
            });

            return unloadedState.PatientLocationStats.patientFacilityCounts;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode
            && !appState.vitalsContext?.PatientLocationStats.isLoaded
            && !appState.vitalsContext?.PatientLocationStats.isLoading) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",

                    Authorization: `Bearer ${accessToken}`,
                }
            }


            let searchTag = practice.tagDescription;
            fetch(`https://${host}/Home/Stats/Locations/Practice/Tag/${searchTag}/`, options)
                .then((response) => response.json() as Promise<PatientFacility[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_LOCATION_STATS",
                        patientFacilityCounts: data

                    });
                })
                .catch((err) => {

                    dispatch({
                        type: "RECEIVE_PATIENTS_LOCATION_STATS_ERROR",
                        errMessage: err.message
                    });

                    console.error(err);
                });

            dispatch({
                type: "REQUEST_PATIENT_STATS_LOCATIONS"
            });
        }

    },

    requestPatientLocationStatsForPractice: (accessToken: string, practiceId: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_PATIENTS_LOCATION_STATS",
                patientFacilityCounts: unloadedState.PatientLocationStats.patientFacilityCounts
            });

            return unloadedState.PatientLocationStats.patientFacilityCounts;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode
            && !appState.vitalsContext?.PatientLocationStats.isLoaded
            && !appState.vitalsContext?.PatientLocationStats.isLoading) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",

                    Authorization: `Bearer ${accessToken}`,
                },
            }

            fetch(`https://${host}/Home/Stats/Locations/Practice/${practiceId}/`, options)
                .then((response) => response.json() as Promise<PatientFacility[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_LOCATION_STATS",
                        patientFacilityCounts: data

                    });
                })
                .catch((err) => {

                    dispatch({
                        type: "RECEIVE_PATIENTS_LOCATION_STATS_ERROR",
                        errMessage: err.message
                    });

                    console.error(err);
                });
        }

        dispatch({
            type: "REQUEST_PATIENTS_LOCATION_STATS"
        });
    },

    requestDischargeDispositionStatsForProvider: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_NSOC_STATS",
                dischargeDispositions: unloadedState.DischargeDispositionStats.dischargeDispositions
            });
            return unloadedState.DischargeDispositionStats.dischargeDispositions;
        }



        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode
            && !appState.vitalsContext.DischargeDispositionStats.isLoaded
            && !appState.vitalsContext.DischargeDispositionStats.isLoading) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            }

            try {
                fetch(`https://${host}/Home/Stats/Discharges/NextSiteOfCare/`, options)
                    .then((response) => response.json() as Promise<DischargeDispositionStatistics[]>)
                    .then((data) => {
                        dispatch({
                            type: "RECEIVE_NSOC_STATS",
                            dischargeDispositions: data
                        });
                    }).catch((err) => {

                        dispatch({
                            type: "RECEIVE_NSOC_STATS_ERROR",
                            errMessage: err.message
                        });

                        console.error(err);
                    });
            } catch (e) {
                dispatch({
                    type: "RECEIVE_NSOC_STATS_ERROR",
                    errMessage: 'Error'
                });
            }
            dispatch({
                type: "REQUEST_NSOC_TYPE_STATS"
            });

        }
    },







    currentUserIsCareCoordinator: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        if (appState?.vitalsContext?.isDemoMode) {
            return true;
        }

        if (appState.vitalsContext?.userPracticeCoordinatorProfile.isLoaded) {
            return false;
        }
    },

    requestDischargeDispositionStatsForByTag: (accessToken: string, practice: Practice): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_NSOC_STATS",
                dischargeDispositions: unloadedState.DischargeDispositionStats.dischargeDispositions
            });
            return appState.vitalsContext?.DischargeDispositionStats.dischargeDispositions;
        }


        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode
            && !appState.vitalsContext.DischargeDispositionStats.isLoaded
            && !appState.vitalsContext.DischargeDispositionStats.isLoading) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            }

            let { tagDescription } = practice;

            try {
                fetch(`https://${host}/Home/Stats/Discharges/NextSiteOfCare/Practice/Tag/${tagDescription}/`, options)
                    .then((response) => response.json() as Promise<DischargeDispositionStatistics[]>)
                    .then((data) => {
                        dispatch({
                            type: "RECEIVE_NSOC_STATS",
                            dischargeDispositions: data
                        });
                    }).catch((err) => {

                        dispatch({
                            type: "RECEIVE_NSOC_STATS_ERROR",
                            errMessage: err.message
                        });

                        console.error(err);
                    });
            } catch (e) {
                dispatch({
                    type: "RECEIVE_NSOC_STATS_ERROR",
                    errMessage: 'Error'
                });
            }

            dispatch({
                type: "REQUEST_NSOC_TYPE_STATS"
            });
        }


    },


    requestDischargeDispositionStatsForPractice: (accessToken: string, practiceId: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_NSOC_STATS",
                dischargeDispositions: unloadedState.DischargeDispositionStats.dischargeDispositions
            });
            return appState.vitalsContext?.DischargeDispositionStats.dischargeDispositions;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode && !appState.vitalsContext.DischargeDispositionStats.isLoaded) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            }

            try {
                fetch(`https://${host}/Home/Stats/Discharges/NextSiteOfCare/Practice/${practiceId}/`, options)
                    .then((response) => response.json() as Promise<DischargeDispositionStatistics[]>)
                    .then((data) => {
                        dispatch({
                            type: "RECEIVE_NSOC_STATS",
                            dischargeDispositions: data
                        });
                    }).catch((err) => {

                        dispatch({
                            type: "RECEIVE_NSOC_STATS_ERROR",
                            errMessage: err.message
                        });

                        console.error(err);
                    });
            } catch (e) {
                dispatch({
                    type: "RECEIVE_NSOC_STATS_ERROR",
                    errMessage: 'Error'
                });
            }

            dispatch({
                type: "REQUEST_NSOC_TYPE_STATS"
            });
        }
    },

    requestAdmitStatsForPracticeByTag: (accessToken: string, practice: Practice): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_REF_MD_ADMIT_STATS",
                admitStats: unloadedState.PhysicianAdmitsStats.physicianAdmits
            });

            return unloadedState.PhysicianAdmitsStats.physicianAdmits;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode
            && !appState.vitalsContext.PhysicianAdmitsStats.isLoaded
            && !appState.vitalsContext.PhysicianAdmitsStats.isLoading) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",

                    Authorization: `Bearer ${accessToken}`,
                },
            }

            let searchTag = practice.tagDescription;
            fetch(`https://${host}/Home/Stats/Admits/Practice/Tag/${searchTag}/`, options)
                .then((response) => response.json() as Promise<AdmitSummaryStatistics[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_REF_MD_ADMIT_STATS",
                        admitStats: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "REQUEST_ADMIT_STATS_ERROR",
                        errMessage: err.message
                    })
                });


            dispatch({
                type: "REQUEST_ADMIT_STATS"
            });
        }
    },

    requestAdmitStatsForPractice: (accessToken: string, practiceId: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();


        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_REF_MD_ADMIT_STATS",
                admitStats: unloadedState.PhysicianAdmitsStats.physicianAdmits
            });

            return unloadedState.PhysicianAdmitsStats.physicianAdmits;
        }


        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode && !appState.vitalsContext.PhysicianAdmitsStats.isLoaded) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",

                    Authorization: `Bearer ${accessToken}`,
                },
            }
            fetch(`https://${host}/Home/Stats/Admits/Practice/${practiceId}/`, options)
                .then((response) => response.json() as Promise<AdmitSummaryStatistics[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_REF_MD_ADMIT_STATS",
                        admitStats: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "REQUEST_ADMIT_STATS_ERROR",
                        errMessage: err.message
                    })
                });


            dispatch({
                type: "REQUEST_ADMIT_STATS"
            });
        }
    },


    requestAdmitTypeStatsForProviderByMonth: (accessToken: string, month: number, year: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_ADMIT_TYPE_STATS",
                admitTypeStats: []
            });

            return;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode && !appState.vitalsContext.PhysicianTypeStats.isLoaded) {
            const monthDate = new Date(year, month - 1, 1).toISOString();
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            }
            fetch(`https://${host}/Home/Stats/Admits/Types/${monthDate}`, options)
                .then((response) => response.json() as Promise<AdmitTypeStatistics[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_ADMIT_TYPE_STATS",
                        admitTypeStats: data
                    });
                })
                .catch((err) => {
                    console.log(err);
                    dispatch({
                        type: "REQUEST_ADMIT_TYPE_STATS_ERROR",
                        errMessage: err.message
                    })
                });

            dispatch({
                type: "REQUEST_ADMIT_TYPE_STATS"
            });
        }
    },


    requestAdmitStatsForProvider: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState && appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_REF_MD_ADMIT_STATS",
                admitStats: unloadedState.PhysicianAdmitsStats.physicianAdmits
            });

            return appState.vitalsContext?.PhysicianAdmitsStats.physicianAdmits;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode && !appState.vitalsContext.PhysicianAdmitsStats.isLoaded) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",

                    Authorization: `Bearer ${accessToken}`,
                },
            }
            fetch(`https://${host}/Home/Stats/Admits/`, options)
                .then((response) => response.json() as Promise<AdmitSummaryStatistics[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_REF_MD_ADMIT_STATS",
                        admitStats: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "REQUEST_ADMIT_STATS_ERROR",
                        errMessage: err.message
                    })
                });


            dispatch({
                type: "REQUEST_ADMIT_STATS"
            });
        }
    },

    requestAdmitStatsForProviderForMonth: (accessToken: string, month: number, year: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState && appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_ADMIT_MONTH_STATS",
                admitStats: unloadedState.PhysicianAdmitMonthStats.physicianAdmits
            });

            return appState.vitalsContext?.PhysicianAdmitMonthStats.physicianAdmits;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode && !appState.vitalsContext.PhysicianAdmitMonthStats.isLoaded) {
            const dateTime = new Date(year, month - 1, 1).toISOString();
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            }
            fetch(`https://${host}/Home/Stats/Admits/Month/${dateTime}`, options)
                .then((response) => response.json() as Promise<AdmitSummaryStatistics[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_ADMIT_MONTH_STATS",
                        admitStats: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "REQUEST_ADMIT_MONTH_STATS_ERROR",
                        errMessage: err.message
                    })
                });


            dispatch({
                type: "REQUEST_ADMIT_MONTH_STATS"
            });
        }
    },

    markMessageAsRead:(accessToken: string, message: Message): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext && appState.vitalsContext.Messages.CurrentMessages) {
            let requestMessage = {
                ...message,
                patient: null
            }

            let request = {
                providerMessage : requestMessage,
                messageParams: appState.vitalsContext.MessageParams
            }

            if (appState && appState.vitalsContext?.isDemoMode) {
                return;
            }

            debugger
            const options = {
                method: "POST",
                headers: {
                    Accept: "application/json",
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(request),
            }

            fetch(`https://${host}/Message/Messages/Read/`, options)
                .then((response): Promise<NotificationCountDto> => {
                    if (!response.ok) {
                        if (response.status === 404) {
                            throw Error('Message not found in mailbox');
                        }
                        throw Error(response.statusText);
                    }

                    return response.json()
                })
                .then((data) => {
                    dispatch({
                        type: 'RECEIVE_NOTIFICATION_UNREAD_COUNT',
                        countStatus: data
                    })
                })
                .catch((error) => {

                });


        }
    },

    requestProviderMessage: (accessToken: string, messageId: number): AppThunkAction<KnownAction> => (dispatch, getState) => {

        const appState = getState();


        if (appState.vitalsContext?.isDemoMode) {
            let messages = unloadedState.Messages.CurrentMessages


            let foundMessage = findMessage(messageId, messages);
            dispatch({
                type: "RECEIVED_FOCUS_MESSAGE",
                Message: foundMessage
            });

            return foundMessage;
        }



        if (appState?.vitalsContext) {
            let messages = appState.vitalsContext.Messages.CurrentMessages;

            let foundMessage = findMessage(messageId, messages);
            if (foundMessage) {

                dispatch({
                    type: "RECEIVED_FOCUS_MESSAGE",
                    Message: foundMessage
                });

                return foundMessage;
            }
            else {
                const options = {
                    method: "GET",
                    headers: {
                        Accept: "application/json",

                        Authorization: `Bearer ${accessToken}`,
                    },
                }

                if (appState.vitalsContext.Messages.isLoaded) {
                    dispatch({
                        type: "REQUEST_FOCUS_MESSAGE"
                    })
                } else {
                    dispatch({
                        type: "REQUEST_FOCUS_MESSAGE_PARTIAL"
                    })
                }


                fetch(`https://${host}/Message/Messages/${messageId}`, options)
                    .then((response): Promise<Message> => {
                        if (!response.ok) {
                            if (response.status === 404) {
                                throw Error('Message not found in mailbox');
                            }
                            throw Error(response.statusText);
                        }

                        return response.json()
                    })
                    .then((data) => {
                        dispatch({
                            type: "RECEIVED_FOCUS_MESSAGE",
                            Message: data
                        });
                    })
                    .catch((error) => {
                        dispatch({
                            type: "RECEIVE_FOCUS_MESSAGE_ERROR",
                            errorMessage: error.message
                        });
                    });
            }
        }

    },

    requestMessagesForProvider: (accessToken: string, daysBack: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_CURRENT_MESSAGES",
                Messages: unloadedState.Messages.CurrentMessages
            });

            return unloadedState.Messages.CurrentMessages;
        }

        let state = appState.vitalsContext?.Messages;

        if (state.isLoading) {
            return;
        }
        let messageParms = {
            daysBack: daysBack
        }


        if (!state?.isLoading) {//&& !state?.isLoaded

            const options = {
                method: "POST",
                body: JSON.stringify(messageParms),
                headers: {
                    Accept: "application/json",
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,

                },
            }
            fetch(`https://${host}/Message/MessagesParams/`, options)
                .then((response) => response.json() as Promise<Message[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_CURRENT_MESSAGES",
                        Messages: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "RECEIVE_CURRENT_MESSAGES_ERROR",
                        ErrorMessage: err.message
                    });
                });

            dispatch({
                type: "REQUEST_CURRENT_MESSAGES"
            });

            dispatch({
                type: "UPDATE_API_HOST",
                apiHost: host
            });
        }


    },

    requestMessageContactsUser: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_USER_PRACTICE_PHYSICIAN_CONTACTS",
                physicianContacts: unloadedState.PatientPhysiciansContacts.userPatientContacts
            });
            return;
        }

        if (appState?.vitalsContext) {
            if (!appState.vitalsContext.PatientPhysiciansContacts.isLoaded && !appState.vitalsContext.PatientPhysiciansContacts.isLoading) {

                let request: PatientListRequest = {
                    MonthsBack: -6,
                    RefMDId: 0,
                    IncludeInactivePatients: false
                };
                const options = {
                    method: "POST",
                    body: JSON.stringify(request),
                    headers: {
                        Accept: "application/json",
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${accessToken}`,

                    },
                }

                fetch(`https://${host}/Patient/Contacts/`, options)
                    .then((response) => response.json() as Promise<HNIProviderContact[]>)
                    .then((data) => {
                        dispatch({
                            type: "RECEIVE_USER_PRACTICE_PHYSICIAN_CONTACTS",
                            physicianContacts: data
                        });
                    })
                    .catch((err) => {
                        dispatch({
                            type: "RECEIVE_CURRENT_MESSAGES_ERROR",
                            ErrorMessage: err.message
                        });
                    });

                dispatch({
                    type: "REQUEST_USER_PRACTICE_PHYSICIAN_CONTACTS"
                });
            }
        }
    },

    requestMessageContactsForPractice: (accessToken: string, practiceId: number): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_USER_PRACTICE_PHYSICIAN_CONTACTS",
                physicianContacts: unloadedState.PatientPhysiciansContacts.userPatientContacts
            });
            return;
        }


        if (appState?.vitalsContext) {
            if (!appState.vitalsContext.PatientPhysiciansContacts.isLoaded && !appState.vitalsContext.PatientPhysiciansContacts.isLoading) {

                const options = {
                    method: "GET",
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${accessToken}`,
                    },
                }

                fetch(`https://${host}/CareCoordinator/Patients/Search/Contacts/id/${practiceId}/`, options)
                    .then((response) => response.json() as Promise<HNIProviderContact[]>)
                    .then((data) => {
                        dispatch({
                            type: "RECEIVE_USER_PRACTICE_PHYSICIAN_CONTACTS",
                            physicianContacts: data
                        });
                    })
                    .catch((err) => {
                        dispatch({
                            type: "RECEIVE_CURRENT_MESSAGES_ERROR",
                            ErrorMessage: err.message
                        });
                    });

                dispatch({
                    type: "REQUEST_USER_PRACTICE_PHYSICIAN_CONTACTS"
                });


            }
        }
    },

    requestSystemSpecialties: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_ALL_SYSTEM_SPECIALTIES",
                AvailableSpecialties: unloadedState.SystemSpecialties.VitalsReferenceSpecialties
            });
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",

                    Authorization: `Bearer ${accessToken}`,
                },
            };
            fetch(`https://${host}/Specialty/available/`, options)
                .then((response) => response.json() as Promise<VitalsReferenceSpecialties>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_ALL_SYSTEM_SPECIALTIES",
                        AvailableSpecialties: data
                    });
                })
                .catch((err) => {
                    console.log(err);
                });

            dispatch({
                type: "REQUEST_AVAILABLE_SPECIALTIES"
            });
        }
    },

    requestCareCoordinatorProfile: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            };
            fetch(`https://${host}/CareCoordinator/Search/`, options)
                .then((response) => response.json() as Promise<CareCoordinatorModel>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_USER_CARE_COORD_PROFILE",
                        coordinatorProfile: data
                    });
                })
                .catch((err) => {
                    console.log(err);
                });

            dispatch({
                type: "REQUEST_USER_CARE_COORD_PROFILE"
            });

        }
    },

    requestCurrentUserCareCoordinatorProfile: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState.vitalsContext?.isDemoMode) {


            dispatch({
                type: "RECEIVE_USER_CARE_COORD_PROFILE",
                coordinatorProfile: unloadedState.userPracticeCoordinatorProfile.coordinatorProfile
            });
            return;
        }

        if (appState && !appState?.vitalsContext.userPracticeCoordinatorProfile.isLoaded
            && !appState.vitalsContext.userPracticeCoordinatorProfile.isLoading) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            };
            fetch(`https://${host}/CareCoordinator/Current/`, options)
                .then((response): Promise<CareCoordinatorModel> => {
                    if (!response.ok) {
                        if (response.status === 404) {
                            dispatch({
                                type: "RECEIVE_USER_CARE_COORD_PROFILE_NOT_FOUND",
                            });

                        }
                        throw Error(response.statusText);
                    };
                    return response.json();
                })
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_USER_CARE_COORD_PROFILE",
                        coordinatorProfile: data
                    });
                })
                .catch((err) => {
                    console.log(err);
                });

            dispatch({
                type: "REQUEST_USER_CARE_COORD_PROFILE"
            });

            dispatch({
                type: "UPDATE_API_HOST",
                apiHost: host
            });

        }
    },

    requestVitalsMdProfile: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_USER_REFMD_PROFILE_NOT_FOUND",
            });
            return;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            };

            fetch(`https://${host}/ReferringMD/id/current/`, options)
                .then((response): Promise<RefMdProfile> => {
                    if (!response.ok) {
                        if (response.status === 404) {
                            dispatch({
                                type: "RECEIVE_USER_REFMD_PROFILE_NOT_FOUND",
                            });

                        }
                        throw Error(response.statusText);
                    }


                    return response.json()
                })
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_USER_REFMD_PROFILE",
                        profile: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "RECEIVE_USER_REFMD_PROFILE_FAILED",
                        error: err
                    });
                });

            dispatch({
                type: "REQUEST_USER_REFMD_PROFILE"
            });

            dispatch({
                type: "UPDATE_API_HOST",
                apiHost: host
            });
        }
    },

    requestPatientsByPracticeIdWithDays: (practiceId: number, coordinatorId: number, daysBack: number, accessToken: string, startDate?: string | null): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();



        if (appState.vitalsContext?.isDemoMode) {
            dispatch({
                type: "RECEIVE_PATIENTS_BY_PRACTICEID",
                practiceId: practiceId,
                patientsByPracticeId: unloadedState.Patients.patientsByPracticeId
            });
            return;
        }

        let request: CareCoordinatorRequest = {
            PracticeId: practiceId,
            PracticeCareCoordinatorId: coordinatorId,
            DaysBack: daysBack,
            AzureADUserName: undefined,
            ...(startDate ? { StartDate: startDate } : {})
        }


        const options = {
            headers: {
                Accept: "application/json",
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,

            },
            method: "POST",
            body: JSON.stringify(request),
        };

        if (!appState.vitalsContext?.Patients.isLoading) {
            fetch(`https://${host}/CareCoordinator/Patients/Search/`, options)
                .then((response) => response.json() as Promise<ListPatient[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_BY_PRACTICEID",
                        practiceId: practiceId,
                        patientsByPracticeId: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_BY_REFMD_ID_ERROR",
                        err: err.message
                    });
                });

            dispatch({
                type: "REQUEST_PATIENTS_BY_PRACTICE_ID",
                practiceId: practiceId
            });
        }
    },

    requestPatientsByRefMDIdWithDays: (refMdId: number, monthsBack: number, includeInactivePatients: boolean, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        /*if (appState.vitalsContext?.isDemoMode) {
            
            dispatch({
                type: "RECEIVE_PATIENTS_BY_REFMD_ID",
                refMdId: refMdId,
                patientsByRefMDId: unloadedState.Patients.patientsByRefMDId
            });

            return unloadedState.Patients.patientsByRefMDId;
        }*/

        if (monthsBack == 0) {
            return;
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode) {

            if (appState.vitalsContext?.Patients.patientsByRefMDId.length > 0) {
                var cachedPts = appState.vitalsContext?.Patients
                    .patientsByRefMDId;

                dispatch({
                    type: "RECEIVE_PATIENTS_BY_REFMD_ID",
                    refMdId: refMdId,
                    patientsByRefMDId: cachedPts
                });

                return cachedPts;
            }

            let request: PatientListRequest = {
                RefMDId: refMdId,
                MonthsBack: monthsBack,
                IncludeInactivePatients: includeInactivePatients
            }

            const options = {
                headers: {
                    Accept: "application/json",
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,

                },
                method: "POST",
                body: JSON.stringify(request),

            };
            fetch(`https://${host}/Patient/Search/`, options)
                .then((response) => response.json() as Promise<ListPatient[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_BY_REFMD_ID",
                        refMdId: refMdId,
                        patientsByRefMDId: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_BY_REFMD_ID_ERROR",
                        err: err.message
                    });
                });
            
            dispatch({
                type: "REQUEST_PATIENTS_BY_REFMD_ID",
                refMdId: refMdId,
            });

            dispatch({
                type: "UPDATE_API_HOST",
                apiHost: host
            });
        }
    },

    requestPatientsByRefMDId: (refMdId: number, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            };
            fetch(`https://${host}/Patient/RefMDId/${refMdId}/ALL`, options)
                .then((response) => response.json() as Promise<ListPatient[]>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_BY_REFMD_ID",
                        refMdId: refMdId,
                        patientsByRefMDId: data
                    });
                })
                .catch((err) => {
                    dispatch({
                        type: "RECEIVE_PATIENTS_BY_REFMD_ID_ERROR",
                        err: err.message
                    });
                });

            dispatch({
                type: "REQUEST_PATIENTS_BY_REFMD_ID",
                refMdId: refMdId,
            });
        }
    },

    requestPatientDocument: (ptid: number, name: string, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        let request: MedicalRecordRequest = {
            PtId: ptid,
            DocumentName: name
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode) {


            const options = {
                headers: {
                    Accept: "application/json",
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${accessToken}`,

                },
                method: "POST",
                body: JSON.stringify(request),

            };


            fetch(`https://${host}/Patient/Record/`, options)
                .then((response) => response.json() as Promise<PatientMedicalRecord>)
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENT_MEDICAL_RECORD_NAME",
                        subjectMedicalRecord: data,
                    });
                })


                .catch((err) => {

                });

            dispatch({
                type: "REQUEST_PATIENT_MEDICAL_RECORD_NAME",
                ptid: ptid,
                name: name
            });
        }

    }
    ,
    updatePatientListParams: (patientListParams: PatientListParams): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        if (appState?.vitalsContext && appState.vitalsContext.isDemoMode) {
            return
        }

        dispatch({
            type: "UPDATE_PATIENT_LIST_PARAMS",
            patientListParams: patientListParams
        });
    },

    updateMessageParams: (messageParams: NotificationParams): AppThunkAction<KnownAction> => (dispatch, getState) => {

        const appState = getState();
        if (appState?.vitalsContext && appState.vitalsContext.isDemoMode) {
            return
        }

        dispatch({
            type: "UPDATE_MESSAGE_PARAMS",
            messageParams: messageParams
        });
    },

    requestConsolidatedPatientById: (patientId: number, accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();

        if (appState?.vitalsContext && appState.vitalsContext.isDemoMode) {
            let samplePatient = null;
            if (patientId === 8268643) {
                samplePatient = unloadedState.Patients.fullPatients[1]
                dispatch({
                    type: "RECEIVE_PATIENT_BY_ID",
                    patientId: patientId,
                    patient: samplePatient
                });

            }

            if (patientId === 124567) {
                samplePatient = unloadedState.Patients.fullPatients[0]
                dispatch({
                    type: "RECEIVE_PATIENT_BY_ID",
                    patientId: patientId,
                    patient: samplePatient
                });


            }

            if (patientId === 23567) {
                samplePatient = unloadedState.Patients.fullPatients[2]
                dispatch({
                    type: "RECEIVE_PATIENT_BY_ID",
                    patientId: patientId,
                    patient: samplePatient
                });
            }

            if (patientId === 123) {
                samplePatient = unloadedState.Patients.fullPatients[3]
                dispatch({
                    type: "RECEIVE_PATIENT_BY_ID",
                    patientId: patientId,
                    patient: samplePatient
                });
            }

            dispatch({
                type: "UPDATE_API_HOST",
                apiHost: host
            });
            return samplePatient;
        }

        if (appState.vitalsContext?.Patients.fullPatients) {

            let fullPatients = appState.vitalsContext?.Patients
                .fullPatients;

            let cachedPt = fullPatients.filter(p => p.patientEncounterId === patientId);
            if (cachedPt && cachedPt.length >= 1) {
                var newFocusPatient = cachedPt[0];
                dispatch({
                    type: "RECEIVE_PATIENT_BY_ID",
                    patientId: patientId,
                    patient: newFocusPatient,
                });
                return newFocusPatient;
            }
        }

        if (appState && appState.vitalsContext && !appState.vitalsContext.isDemoMode) {
            const options = {
                method: "GET",
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            };
            fetch(`https://${host}/Patient/id/${patientId}/`, options)
                .then((response): Promise<Patient> => {
                    if (!response.ok) {
                        throw Error(response.statusText);
                    }
                    return response.json();
                })
                .then((data) => {
                    dispatch({
                        type: "RECEIVE_PATIENT_BY_ID",
                        patientId: patientId,
                        patient: data,
                    });
                }).catch((err) => {
                    console.log(err);
                });

            dispatch({
                type: "REQUEST_PATIENT_BY_ID",
                patientId: patientId,
            });
        }
    },

    clearMessageState: (): AppThunkAction<KnownAction> => (dispatch, getState) => {
        dispatch({
            type: "CLEAR_SEND_MESSAGE_RESPONSE",
        });
    },
    requestBlobById: (accessToken: string): AppThunkAction<KnownAction> => (dispatch, getState) => {
        const appState = getState();
        const options = {
            method: "GET",
            headers: {
                Accept: "application/json",
                Authorization: `Bearer ${accessToken}`,
            },
        };

        fetch(`https://${host}/api/AzureStorage/GetBlob/`, options)
            .then((data) => {
                console.log(data);
            }).catch((err) => {
                console.log(err);
            });
    }
};

// ----------------
// REDUCER - For a given state and action, returns the new state. To support time travel, this must not mutate the old state.



const janeId = 8268643;
const janeMbi = '4AG4-GE5-KJ81'
const johnMbi = '1EG4-TE5-MK73'


const johnId = 124567;
const jayId = 23567;
const jayMbi = '7CG4-RB3-NJ52'
const abbyId = 123;
const abbyMbi = '2BD1-ED8-MI44'

const testPatientData: ListPatient[] = [
    {
        mrn: 'L000000093',
        firstName: 'John',
        lastName: 'Doe',
        pcn: '9641947463452',
        ptId: johnId,
        patientEncounterId: johnId,
        mbi: johnMbi,
        facilityId: 0,
        payer: 'AMBETTER',
        primaryCareProvider: "PENALO, PEDRO",
        roomNumber: 'H.3W-H.354-A',
        dateOfBirth: '01/01/1960',
        admitDate: '01/04/2025',
        dischargeDate: '03/06/2025',

        daysToDischarge: 0,
        primaryProviderContact: {
            email: 'Jack.Kefler@hnihc.com',
            displayName: 'Jack Kefler',
            type: 'Assigned Provider',
            lastName: ""
        },
        referringMdUserName: 'doctor.smith@hospnow.com',
        recentAdmits: 2,
        facilityName: 'Rio Grande Regional Inpatient'

    },
    {
        mrn: '854942197',
        firstName: 'Jane',
        lastName: 'Roe',
        pcn: '97989847',
        mbi: janeMbi,
        ptId: janeId,
        patientEncounterId: janeId,
        facilityId: 0,
        primaryCareProvider: 'TORKILDSEN, WILLIAM',
        payer: 'ALLWELL',
        roomNumber: 'VR.2E OBS1-VR.252-B',
        facilityName: 'Valley Regional Meical Demo',
        dateOfBirth: '01/01/1944',
        admitDate: '2/21/2025',
        dischargeDate: '02/23/2025',
        daysToDischarge: 0,
        primaryProviderContact: {
            email: 'Jack.Kefler@hnihc.com',
            displayName: 'Jack Kefler',
            type: 'Assigned Provider',
            lastName: ""
        },
        referringMdUserName: 'doctor.smith@hospnow.com',
        recentAdmits: 0,
    },
    {
        mrn: 'AE-187723-Q4',
        firstName: 'Jaylen',
        lastName: 'Watts',
        pcn: 'JM05531595',
        mbi: jayMbi,
        ptId: jayId,
        primaryCareProvider: 'MCDOUGAL, PEDRO',
        patientEncounterId: jayId,
        facilityId: 0,
        payer: 'WELLMED',
        facilityName: 'Weslaco Nursing and Rehab',
        dateOfBirth: '01/01/1951',
        admitDate: '1/9/2025',

        daysToDischarge: 4,
        primaryProviderContact: {
            email: 'Eillen.Ward@hnihc.com',
            displayName: 'Eillen Ward',
            type: 'Assigned Provider',
            lastName: ""
        },
        referringMdUserName: 'Jack.Kefler@hnihc.com',
        recentAdmits: 0,
    },
    {
        mrn: '4001-Q4H1156',
        firstName: 'Abbigail',
        lastName: 'Larson',
        pcn: 'M059519',
        mbi: abbyMbi,
        ptId: abbyId,
        patientEncounterId: abbyId,
        facilityId: 0,
        primaryCareProvider: 'OSUNA SALAZAR, ELVA',
        payer: 'CIGNA MA',
        facilityName: 'The Heights of Alamo',
        dateOfBirth: '01/01/1959',
        admitDate: '2/2/2025',
        daysToDischarge: 2,
        primaryProviderContact: {
            email: 'Eillen.Ward@hnihc.com',
            displayName: 'Eillen Ward',
            type: 'Assigned Provider',
            lastName: ""
        },
        referringMdUserName: 'Jack.Kefler@hnihc.com',
        recentAdmits: 2,
    }
]



let janeDx1: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'K59.09',
        codingSystem: 'ICD10CM',
        dxId: 445,
        isRiskAdjusted: false,
        hccCategory: ""
    },
    shortDescription: 'Other constipation',
    hccCategory: ''
}


let janeDx2: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'I50.9',
        codingSystem: 'ICD10CM',
        dxId: 446,
        isRiskAdjusted: true,
        hccCategory: '85'
    },
    shortDescription: 'Heart failure, unspecified',

}
let janeDx22: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'I48.0',
        codingSystem: 'ICD10CM',
        dxId: 446,
        isRiskAdjusted: true,
        hccCategory: '96'
    },
    shortDescription: 'Paroxysmal atrial fibrillation ',

}


let janeDx3: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: ' D69.49',
        codingSystem: 'ICD10CM',
        dxId: 447,
        isRiskAdjusted: true,
        hccCategory: '48'
    },
    shortDescription: 'Other primary thrombocytopenia',

}

let janeDx4: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'I10',
        codingSystem: 'ICD10CM',
        dxId: 448,
        isRiskAdjusted: false,
        hccCategory: ""
    },
    shortDescription: 'Essential (primary) hypertension',
    riskAdjusted: false,
    hccCategory: ''
}

let janeDx5: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'E55.9',
        codingSystem: 'ICD10CM',
        dxId: 448,
        isRiskAdjusted: false,
        hccCategory: ""
    },
    shortDescription: 'Vitamin D deficiency, unspecified',
    isRiskAdjusted: false,
    hccCategory: ''
}

let janeDx6: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'R94.6',
        codingSystem: 'ICD10CM',
        dxId: 448,
        isRiskAdjusted: false,
        hccCategory: ""
    },
    shortDescription: 'Abnormal results of thyroid function studies',
    riskAdjusted: false,
    hccCategory: ''
}



let johnDx1: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'R12',
        codingSystem: 'ICD10CM',
        dxId: 449,
        isRiskAdjusted: false,
        hccCategory: ""
    },
    shortDescription: 'Heartburn',
    riskAdjusted: false,
    hccCategory: ''
}

let johnDx2: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'R45.851',
        codingSystem: 'ICD10CM',
        dxId: 450,
        isRiskAdjusted: false,
        hccCategory: ""
    },
    shortDescription: 'Suicidal ideations',
    riskAdjusted: false,
    hccCategory: ''
}

let johnDx3: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'F33.9',
        codingSystem: 'ICD10CM',
        dxId: 451,
        isRiskAdjusted: true,
        hccCategory: '59'

    },
    shortDescription: 'Major depressive disorder, recurrent, unspecified',

}


let johnDx4: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'Z94.2',
        codingSystem: 'ICD10CM',
        dxId: 452,
        isRiskAdjusted: true,
        hccCategory: '186'

    },
    shortDescription: 'Lung transplant status',

}

let johnDx5: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'D84.9',
        codingSystem: 'ICD10CM',
        dxId: 453,
        isRiskAdjusted: true,
        hccCategory: '47'

    },
    shortDescription: 'Immunodeficiency, unspecified',

}

let johnDx6: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'I25.10',
        codingSystem: 'ICD10CM',
        dxId: 454,
        isRiskAdjusted: false,
        hccCategory: ""
    },
    shortDescription: 'Atherosclerotic heart disease of native coronary artery without angina pectoris',
    riskAdjusted: false,
    hccCategory: ''
}


let abbyDx1: PatientDiagnosis = {
    diagnosedBy: 'jules.hodge@hnihc.com',

    diagnosis: {
        diagnosisCodeValue: 'F02.80',
        codingSystem: 'ICD10CM',
        dxId: 455,
        isRiskAdjusted: true,
        hccCategory: '52'

    },
    shortDescription: 'Dementia In Other Diseases Classified Elsewhere, Unspecified Severity, Without Behavioral Disturbance, Psychotic Disturbance, Mood Disturbance, And Anxiety',

}

let testDocumentHpJane: DownloadablePatientDocument = {
    name: 'Jane_Roe_HP_AustinMemorial.pdf',
    displayName: 'History And Physical',
    createdBy: 'Reuben W. Tovar, MD',
    createdDate: '2024-02-22T08:19:56.73',
    extension: 'pdf',
    path: 'enotes',
    id: 'eNoteService'
}


let testDocumentHpJaneHPHistorial: DownloadablePatientDocument = {
    name: 'HP_RapidsRegional.pdf',
    displayName: 'History And Physical',
    createdBy: 'Reuben W. Tovar, MD',
    createdDate: '2023-01-22T08:19:56.73',
    extension: 'pdf',
    path: 'enotes',
    id: 'eNoteService'
}

let testDocumentDcJaneHistorical: DownloadablePatientDocument = {
    name: 'DC_BlankRegional.pdf',
    displayName: 'Discharge Summary',
    createdBy: 'Reuben W. Tovar, MD',
    createdDate: '2023-02-23T08:19:56.73',
    extension: 'pdf',
    path: 'enotes',
    id: 'eNoteService'
}

let testDocumentDcJane: DownloadablePatientDocument = {
    name: 'Jane_Roe_DC_AustinMemorial.pdf',
    displayName: 'Discharge Summary',
    createdBy: 'Reuben W. Tovar, MD',
    createdDate: '2024-02-23T08:19:56.73',
    extension: 'pdf',
    path: 'enotes',
    id: 'eNoteService'
}

let testDocumentPnJane: DownloadablePatientDocument = {
    name: 'Jane_Roe_PN_AustinMemorial.pdf',
    displayName: 'Progress Note',
    createdBy: 'Reuben W. Tovar, MD',
    createdDate: '2024-02-22T08:19:56.73',
    extension: 'pdf',
    path: 'enotes',
    id: 'eNoteService'
}

let testDocumentDcJohn: DownloadablePatientDocument = {
    name: 'John_Doe_Hosp_DC.pdf',
    displayName: 'Discharge Summary',
    createdBy: 'Reuben W. Tovar, MD',
    createdDate: '2024-03-06T08:19:56.73',
    extension: 'pdf',
    path: 'enotes',
    id: 'eNoteService'
}


let testDocumentPnJohn: DownloadablePatientDocument = {
    name: 'John_Doe_PN.pdf',
    displayName: 'Progress Note',
    createdBy: 'Reuben W. Tovar, MD',
    createdDate: '2024-03-03T08:19:56.73',
    extension: 'pdf',
    path: 'enotes',
    id: 'eNoteService'
}


let testDocumentHpJohn: DownloadablePatientDocument = {
    name: 'John_Doe_HP.pdf',
    displayName: 'History And Physical',
    createdBy: 'Reuben W. Tovar, MD',
    createdDate: '2024-03-03T08:19:56.73',
    extension: 'pdf',
    path: 'enotes',
    id: 'eNoteService'
}


let testEncounterJane: Encounter = {
    encounterId: janeId,
    patientEncounterId: janeId,
    ptId: janeId,

    encounterStart: '02/21/2024',
    encounterEnd: '02/23/2024',
    dateOfBirth: '01/01/1944',
    primaryProviderContact: {
        email: 'Jack.Kefler@hnihc.com',
        displayName: 'Jack Kefler',
        type: 'Assigned Provider',
        lastName: 'Kefler'
    },
    referringMD: {
        lastName: 'Smith',
        email: 'Ryan.Smith@hnihc.com',
        displayName: 'Ryan Smith',
        type: 'Assigned Provider',
    },
    mrn: 'mrn',
    daysToDischarge: 0,
    mbi: janeMbi,
    facility: {
        facilityName: 'Rio Grande Regional Hospital - Inpatient Rehab',
        facilityId: 10000001,
        facilityProgramManager: {
            name: 'Jules Hodge',
            email: 'jules.hodge@hnihc.com'
        }
    },
    patientDiagnosisList: [janeDx1, janeDx2, janeDx22, janeDx3, janeDx4, janeDx5, janeDx6],
    downloadablePatientDocuments: [testDocumentHpJane, testDocumentPnJane, testDocumentDcJane],
    patientChargeList: [],
    encounterInsurance: {
        companyNameFromFacility: 'Medicare'
    }
}

let testEncounterJohn1: Encounter = {
    encounterId: johnId,
    patientId: johnId,
    encounterStart: '03/03/2024',
    encounterEnd: '03/06/2024',
    dateOfBirth: '01/01/1960',
    primaryProviderContact: {
        email: 'Eillen.Ward@hnihc.com',
        displayName: 'Eileen Ward',
        type: 'Assigned Provider',
        lastName: 'Ward '
    },
    referringMD: {
        lastName: 'Smith',
        email: 'Ryan.Smith@hnihc.com',
        displayName: 'Ryan Smith',
        type: 'Assigned Provider',
    },
    mrn: '4849216126',
    mbi: johnMbi,
    daysToDischarge: 0,
    facility: {
        facilityName: 'Rio Grande Regional Hospital - Inpatient Rehab',
        facilityId: 10000001,
        facilityProgramManager: {
            name: 'Jules Hodge',
            email: 'jules.hodge@hnihc.com'
        }
    },
    patientDiagnosisList: [johnDx1, johnDx2, johnDx3, johnDx4, johnDx5, johnDx6],
    patientChargeList: [],
    downloadablePatientDocuments: [testDocumentDcJohn, testDocumentPnJohn, testDocumentHpJohn],
    encounterInsurance: {
        companyNameFromFacility: 'Medicare'
    }
}

let testEncounterJohn2: Encounter = {
    encounterId: 5567331,
    patientId: 5567331,
    encounterStart: '9/5/2021',
    encounterEnd: '9/6/2021',
    dateOfBirth: '01/01/1960',
    primaryProviderContact: {
        email: 'Jack.Kefler@hnihc.com',
        displayName: 'Jack Kefler',
        type: 'Assigned Provider',
        lastName: 'Kefler'
    },
    referringMD: {
        lastName: 'Smith',
        email: 'Ryan.Smith@hnihc.com',
        displayName: 'Ryan Smith',
        type: 'Assigned Provider',
    },
    mrn: 'mrn',
    mbi: johnMbi,
    daysToDischarge: 2,
    facility: {
        facilityName: 'Valley Regional Medical Center',
        facilityId: 10000001,
        facilityProgramManager: {
            name: 'Jules Hodge',
            email: 'jules.hodge@hnihc.com'
        }
    },
    patientDiagnosisList: [johnDx1, johnDx2, johnDx3, johnDx4, johnDx5],
    patientChargeList: [],
    downloadablePatientDocuments: [testDocumentDcJohn],
    encounterInsurance: {
        companyNameFromFacility: 'Medicare'
    }
}


let testEncounterJay: Encounter = {
    encounterId: jayId,
    patientId: jayId,
    encounterStart: '1/9/2025',
    encounterEnd: '1/17/2025',
    dateOfBirth: '01/01/1951',
    primaryProviderContact: {
        email: 'Jack.Kefler@hnihc.com',
        displayName: 'Jack Kefler',
        type: 'Assigned Provider',
        lastName: 'Kefler'
    },
    referringMD: {
        lastName: 'Smith',
        email: 'Ryan.Smith@hnihc.com',
        displayName: 'Ryan Smith',
        type: 'Assigned Provider',
    },
    mrn: 'AE-187723-Q4',
    mbi: jayMbi,
    daysToDischarge: 4,
    facility: {
        facilityName: 'Golden Palms Retirement and Health Center',
        facilityId: 10000001,
        facilityProgramManager: {
            name: 'Jules Hodge',
            email: 'jules.hodge@hnihc.com'
        }
    },
    patientDiagnosisList: [johnDx2, johnDx4],
    patientChargeList: [],
    downloadablePatientDocuments: [],
    encounterInsurance: {
        companyNameFromFacility: 'Medicare'
    }
}


let testEncounterAby: Encounter = {
    encounterId: abbyId,
    patientId: abbyId,
    encounterStart: '02/02/2025',
    dateOfBirth: '01/01/1959',
    primaryProviderContact: {
        email: 'larry.david@hnihc.com',
        displayName: 'Larry David',
        type: 'Assigned Provider',
        lastName: 'David'
    },
    referringMD: {
        lastName: 'Smith',
        email: 'Ryan.Smith@hnihc.com',
        displayName: 'Ryan Smith',
        type: 'Assigned Provider',
    },
    mrn: '4001-Q4H1156',
    mbi: abbyMbi,
    daysToDischarge: 2,
    facility: {
        facilityName: 'Midland Memorial Hospital',
        facilityId: 10000001,
        facilityProgramManager: {
            name: 'Jules Hodge',
            email: 'jules.hodge@hnihc.com'
        }
    },
    patientDiagnosisList: [abbyDx1, johnDx4],
    patientChargeList: [],
    downloadablePatientDocuments: [],
    encounterInsurance: {
        companyNameFromFacility: 'Medicare'
    }
}

let testEncounterAby2: Encounter = {
    encounterId: abbyId,
    patientId: abbyId,
    encounterStart: '07/22/2021',
    encounterEnd: '07/29/2021',
    dateOfBirth: '01/01/1959',
    primaryProviderContact: {
        email: 'Jack.Kefler@hnihc.com',
        displayName: 'Jack Kefler',
        type: 'Assigned Provider',
        lastName: 'Kefler'
    },
    referringMD: {
        lastName: 'Smith',
        email: 'Ryan.Smith@hnihc.com',
        displayName: 'Ryan Smith',
        type: 'Assigned Provider',
    },
    mrn: '4001-Q4H1156',
    mbi: abbyMbi,
    daysToDischarge: 0,
    facility: {
        facilityName: 'Midland Memorial Hospital',
        facilityId: 10000001,
        facilityProgramManager: {
            name: 'Jules Hodge',
            email: 'jules.hodge@hnihc.com'
        }
    },
    patientDiagnosisList: [abbyDx1, johnDx4],
    patientChargeList: [],
    downloadablePatientDocuments: [],
    encounterInsurance: {
        companyNameFromFacility: 'Medicare'
    }
}




let samplePatientJane: Patient = {
    firstName: 'Jane',
    lastName: 'Roe',
    patientEncounterId: janeId,
    ptId: janeId,

    mrn: '854942197',
    pcn: 97989847,
    mbi: janeMbi,
    facilityId: 0,
    primaryProviderContact: {
        email: 'Jack.Kefler@hnihc.com',
        displayName: 'Jack Kefler',
        type: 'Assigned Provider',
        lastName: 'Kefler'
    },
    facilityName: `Rio Grande Regional Hospital`,
    insuranceName: 'Medicare',
    dateOfBirth: ' 01/01/1944',
    dateOfAdmission: '2/5/2025',
    encounters: [testEncounterJane],
    historicalDocuments: [testDocumentDcJaneHistorical, testDocumentHpJaneHPHistorial],
    

    downloadablePatientDocuments: [testDocumentHpJane, testDocumentPnJane]
}

let samplePatientJohn: Patient = {
    firstName: 'John',
    lastName: 'Doe',
    patientEncounterId: johnId,
    ptId: johnId,
    mrn: '4849216126',
    mbi: johnMbi,
    facilityId: 0,
    primaryProviderContact: {
        email: 'Jack.Kefler@hnihc.com',
        displayName: 'Jack Kefler',
        type: 'Assigned Provider',
        lastName: 'Kefler'
    },
    facilityName: `Rio Grande Regional Hospital`,
    insuranceName: 'Medicare',
    dateOfBirth: '01/01/1960',
    dateOfAdmission: '2/5/2015',
    encounters: [testEncounterJohn1, testEncounterJohn2],
    downloadablePatientDocuments: [testDocumentDcJohn]
}

let samplePatientJaylen: Patient = {
    firstName: 'Jaylen',
    lastName: 'Watts',
    patientEncounterId: jayId,
    ptId: jayId,
    mrn: 'AE-187723-Q4',
    mbi: jayMbi,
    facilityId: 0,
    primaryProviderContact: {
        email: 'Jack.Kefler@hnihc.com',
        displayName: 'Jack Kefler',
        type: 'Assigned Provider',
        lastName: 'Kefler'
    },
    facilityName: `Golden Palms Retirement and Health Center`,
    insuranceName: 'Medicare',
    dateOfBirth: '01/01/1960',
    dateOfAdmission: '2/5/2025',
    encounters: [testEncounterJay],
    downloadablePatientDocuments: [testDocumentDcJohn]
}

let samplePatientAbby: Patient = {
    firstName: 'Abbigail',
    lastName: 'Larson',
    patientEncounterId: abbyId,
    ptId: abbyId,

    mrn: 'AE-187723-Q4',
    mbi: abbyMbi,
    facilityId: 0,
    primaryProviderContact: {
        email: 'Jack.Kefler@hnihc.com',
        displayName: 'Jack Kefler',
        type: 'Assigned Provider',
        lastName: 'Kefler'
    },
    facilityName: `Midland Memorial Hospital`,
    insuranceName: 'Medicare',
    dateOfBirth: '01/01/1959',
    dateOfAdmission: '2/2/2025',
    dateOfDischarge: '2/10/2025',
    encounters: [testEncounterAby, testEncounterAby2],
    downloadablePatientDocuments: []
}



let globalIsDemoMode: boolean = false;

export function getGlobalIsDemoMode(): boolean {
    return globalIsDemoMode;
}


const emptyState: HNIContextState = {
    isLoading: false,
    isDemoMode: globalIsDemoMode,
    PatientPhysicians: [],
    NotificationCount: {
        isLoaded: false,
        isLoading: false,
        isErrored: false,
        UnreadCount: 0
    },
    Patients: {
        patientsByRefMDId: [],
        isLoading: false,
        facilityId: 0,
        refMdId: 0,
        fullPatients: []
    },
    PatientLocationStats: {
        isLoaded: false,
        isLoading: false,
        isErrored: false,
        error: '',
        patientFacilityCounts: []
    },
    PatientFacilityDrillDownStats: {
        isLoaded: false,
        isLoading: false,
        isErrored: false,
        error: '',
        patientFacilityDrillDownCounts: null
    },
    DischargeDispositionStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: false,
        error: '',
        dischargeDispositions: []
    },
    PhysicianAdmitsStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: false,
        error: '',
        physicianAdmits: []
    },
    PhysicianAdmitMonthStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: false,
        error: '',
        physicianAdmits: []
    },
    PhysicianTypeStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: false,
        error: '',
        admitTypeStats: []
    },
    Messages: {
        isLoading: false,
        isLoaded: false,
        Errors: [],
        ReplyOptions: ["Reply To ", "Message Physician", "Message Physician Management"],
        CurrentMessages: [],
        failedLoading: false,
        FocusMessage: undefined,
        SendMessageState: undefined
    },
    userPracticeCoordinatorProfile: {
        notFound: false,
        isLoaded: false,
        isLoading: false,
        isErrored: false,
        coordinatorProfile: {
            practiceId: 0
        }
    },
    userRefMdProfile: {
        notFound: false,
        isLoaded: false,
        isLoading: false,
        isErrored: false,
        refmdProfile: {
            referringMDId: null
        }
    },
    SystemSpecialties: {
        isLoading: false,
        isLoaded: false,
        VitalsReferenceSpecialties: {
            specialties: []
        }
    },
    PatientPhysiciansContacts: {
        isLoaded: false,
        isLoading: false,
        userPatientContacts: [],
        isErrored: false,
        error: ''
    },
    APIToken: null,
    PatientListParams: {
        daysBack: 30,
        columnFilterMap: new Map()

    },
    MessageParams: {
        daysBack: 30,
    }
}

const unloadedState: HNIContextState = {
    isLoading: false,
    isDemoMode: globalIsDemoMode,
    Patients: {
        patientsByRefMDId: testPatientData,
        patientsByPracticeId: testPatientData,
        fullPatients: [samplePatientJohn, samplePatientJane, samplePatientJaylen, samplePatientAbby],
        isLoading: false,
        isLoaded: true,
        facilityId: 3,
        refMdId: 194,
        isError: false,
        errorMessage: ""
    },
    PatientPhysicians: [
        {
            formattedName: 'Jules Hodge',
            emailAddress: 'jules.hodge@hnihc.com'
        },
        {
            formattedName: 'Eillen Ward',
            emailAddress: 'Eillen.Ward@hnihc.com'
        },
        {
            formattedName: 'Arnold',
            emailAddress: 'arnold.dealejandro@hnihc.com'
        },
        {
            formattedName: 'Arnold',
            emailAddress: 'arnold.dealejandro@hnihc.com'
        },
        {
            formattedName: 'Frank Thomas',
            emailAddress: 'frank.thomas@hnihealthcare.com'
        },
        {
            formattedName: 'Larry David',
            emailAddress: 'larry.david@hnihealthcare.com'
        },
        {
            formattedName: 'Ryan Smith',
            emailAddress: 'Ryan.Smith@hnihc.com'
        },
        {
            formattedName: 'Jim Bones',
            emailAddress: 'bones.jim@hnihc.com'
        }
    ],
    DischargeLocationStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: true,
        error: '',
        DischargeLocations: [{
            startFacilityId: 3,
            dischargeFacilityName: `St. Joseph's`,
            dischargeCount: 21
        }, {
            startFacilityId: 3,
            dischargeFacilityName: 'Herman Memorial',
            dischargeCount: 15
        }, {
            startFacilityId: 3,
            dischargeFacilityName: 'Seton Health',
            dischargeCount: 15
        }, {
            startFacilityId: 8,
            dischargeFacilityName: 'Sacred Heart',
            dischargeCount: 2
        }, {
            startFacilityId: 156,
            dischargeFacilityName: 'Sacred Heart',
            dischargeCount: 92
        }]
    },
    PatientLocationStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: true,
        error: '',
        patientFacilityCounts: [
            {
                facilityName: 'Rio Grande RGNL. Hospital',
                facilityId: 3,
                patientsCount: 4
            }, {
                facilityName: 'Solara Harlingen - PP',
                facilityId: 3,
                patientsCount: 7
            }, {
                facilityName: 'The Heights of Alamo',
                facilityId: 3,
                patientsCount: 12
            }
        ]
    },
    DischargeDispositionStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: true,
        error: '',
        dischargeDispositions: [{
            typeDesciption: 'Skilled Nursing Facility (SNF)',
            patientCount: 9
        }, {
            typeDesciption: 'Acute Rehab Hospital',
            patientCount: 25
        }, {
            typeDesciption: 'Psych Hospital/Unit',
            patientCount: 4
        }, {
            typeDesciption: 'AMA',
            patientCount: 2
        }]
    },
    PhysicianAdmitsStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: true,
        error: '',
        physicianAdmits: [{
            totalAdmits: 1,
            AssignedMDUserName: 'Rebecca Cooper',
            year: 24,
            month: 10,
            referringMDId: '13000'
        },
        {
            totalAdmits: 7,
            month: 11,
            AssignedMDUserName: 'Welena Qazizada',
            year: 24,
            referringMDId: '13000'
        },
        {
            totalAdmits: 3,
            month: 12,
            AssignedMDUserName: 'Matthew Rossmann',
            year: 24,
            referringMDId: '13000'
        },
        {
            totalAdmits: 12,
            month: 1,
            AssignedMDUserName: 'Osman.Ismaeel',
            year: 25,
            referringMDId: '13000'
        },
        {
            totalAdmits: 3,
            month: 2,
            year: 25,
            AssignedMDUserName: 'William Yates',
            referringMDId: '13000'
        },
        {
            totalAdmits: 12,
            month: 3,
            year: 25,
            AssignedMDUserName: 'Rajiv Doddamani',
            referringMDId: '13000'
        }
        ]
    },
    PhysicianTypeStats: {
        isLoading: false,
        isErrored: false,
        isLoaded: false,
        error: '',
        admitTypeStats: []
    },


    Messages: {
        isLoading: false,
        isLoaded: true,
        FocusMessage: {
            isLoading: false,
            isErrored: false,
            messageId: 101,
            errorMessage: '',

            message: {
                subject: 'Jane dx codes',
                senderId: 'doctor.thomas@hnihealthcare.com',
                sendDate: '2/5/2025',
                createdDate: '2/05/2025',
                messageId: 101,
                message: 'We have updated the diagnosis bundle for your patient.  Let me know if you have any questions.',
                parentMessageId: 0,
                receiverId: 'jules.hodge@hospnow.com',
                status: '0',
                description: 'user',
                patientLastName: 'Doe',
                ptId: janeId,
                documentFileName: '',
                messageReadDate: '11/05/2021',
                document: undefined
            }
        },
        Errors: [],
        ReplyOptions: ["Reply To ", "Message Physician", "Message Physician Management"],
        CurrentMessages: [

            {
                subject: 'Jane dx codes',
                senderId: 'doctor.thomas@hnihealthcare.com',

                sendDate: '2/6/2025',
                createdDate: '2/06/2025',
                messageId: 101,
                message: 'We have updated the diagnosis bundle for your patient.  Let me know if you have any questions.',
                parentMessageId: 0,
                receiverId: 'jules.hodge@hospnow.com',
                status: '0',
                patientId: '8268643',
                ptId: janeId,
                description: 'user',
                patientLastName: 'Thomas',
                patientFirstName: 'Bill',
                documentFileName: '',
                messageReadDate: '11/05/2021',
                document: undefined
            },
            {
                subject: 'Patient Admit: Roe, Jane',
                senderId: 'system',
                sendDate: '2/04/2025',
                createdDate: '2/04/2025',
                messageId: 100,
                message: 'Dear Dr. HodgeSupport. Thank you for the referral of the following patient: MIPSTEST, Jules at HNI Healthcare on 1/2/2025. Date of Birth: . Current diagnosis codes: Z20.828, E11.65. Assigned MD: Jules Hodge. Respectfully, HNI Healthcare. Phone (Ph: 956-440-6300) Fax (Fax: (888) 570-1735).',
                parentMessageId: 0,
                receiverId: 'jules.hodge@hospnow.com',
                status: '0',
                patientId: janeId,
                ptId: janeId,
                description: 'admit',
                patientLastName: 'Doe',
                patientFirstName: 'Jane',
                documentFileName: '',
                messageReadDate: '11/05/2021',
                document: undefined
            },
            {
                subject: 'Discharge Notification: Doe, John',
                senderId: 'system',
                sendDate: '02/13/2025',
                createdDate: '02/13/2025',
                messageId: 201,
                message: 'Dear Dr. HodgeSupport. Thank you for the referral of the following patient: Doe, John at HNI Healthcare on 1/2/2025. Date of Birth: . Current diagnosis codes: Z20.828, E11.65. Assigned MD: Jules Hodge. Respectfully, HNI Healthcare. Phone (Ph: 956-440-6300) Fax (Fax: (888) 570-1735).',
                parentMessageId: 0,
                receiverId: 'jules.hodge@hospnow.com',
                status: '0',
                ptId: johnId,
                patientId: johnId,
                description: 'discharge',
                patientLastName: 'Doe',
                patientFirstName: 'John',
                messageReadDate: '1/05/2025',
                documentFileName: '',
                document: undefined
            }
        ]
    },
    userRefMdProfile: {
        isLoaded: false,
        isErrored: false,
        isLoading: false,
        error: '',
        notFound: false,
        refmdProfile: undefined,
    },
    userPracticeCoordinatorProfile: {
        notFound: false,
        isLoaded: true,
        isLoading: false,
        error: '',
        isErrored: false,
        coordinatorProfile: {
            firstName: 'Frank',
            lastName: 'Thomas',
            practiceCareCoordinatorId: 5,
            defaultPractice: {
                practiceName: "Kaizen Clinical Partners",
                practiceId: 5,
                tagDescription: 'KCP',
                isRosterPractice: true
            },
            azureAdAccount: 'frankDemo@glennwood.medical.org',
            practices: [{
                practiceName: "Kaizen Clinical Partners",
                practiceId: 5,
                tagDescription: 'KCP',
                isRosterPractice: true
            }],
            emailPreferred: true,
            textPreferred: true,
            notifyAdmits: true,
            notifyDischarges: true,
            cell: '611-981-0214',
            phone: '611-981-0214',

        }
    },
    AllMessageTypes: [{
        messageTypeId: 1,
        description: 'a type'
    }],
    SystemSpecialties: {
        isLoading: false,
        isLoaded: true,
        VitalsReferenceSpecialties: {
            specialties: [{
                specialtyId: '1',
                description: 'Neurosurgery'
            }, {
                specialtyId: '2',
                description: 'Orthopedic Surgery'
            }, {
                specialtyId: '3',
                description: 'Vascular Surgery'
            }, {
                specialtyId: '4',
                description: 'Cardiology'
            }, {
                specialtyId: '5',
                description: 'Geriatric Psychiatry'
            }, {
                specialtyId: '6',
                description: 'Dietitian'
            }]
        }
    },

    NotificationCount: {
        isLoaded: false,
        isErrored: true,
        isLoading: false,
        UnreadCount: 0

    },

    PatientPhysiciansContacts: {
        isLoaded: false,
        isLoading: false,
        userPatientContacts: [{
            displayName: 'Dr. Jack Kefler',
            email: 'Jack.Kefler@hnihc.com',
            type: 'Assigned Provider',
            lastName: 'Kefler'
        },
        {
            displayName: 'Dr. Eillen Ward',
            email: 'Eillen.Ward@hnihc.com',
            type: 'Assigned Provider',
            lastName: 'Ward'
        },
        {
            displayName: 'Dr. Ryan Smith',
            email: 'Ryan.Smith@hnihc.com',
            type: 'Assigned Provider',
            lastName: 'Smith'
        },
        {
            displayName: 'Dr. Frank Thomas',
            email: 'frank.thomas@hnihealthcare.com',
            type: 'Assigned Provider',
            lastName: 'Thomas'
        },
        {
            displayName: 'Dr. Larry David',
            email: 'larry.david@hnihc.com',
            type: 'Assigned Provider',
            lastName: 'David'
        }],
        isErrored: false,
        error: ''

    },


    FocusPatient: {
        patientId: 12642976,
        patient: samplePatientJane,
        isLoading: false
    },
};

// Not sure if this is best location for data helper methods
const removeMessage = (message: Message, messageList: Message[]) => {
    if (message.status === 'ARCHIVED') {
        return messageList.filter(val => val.messageId !== message.messageId);
    }

    return messageList;
}

const removeMessages = (deletedMessages: Message[], messageList: Message[]) => {
    let retList = messageList;
    for (let delMsg of deletedMessages) {
        retList = removeMessage(delMsg, retList)
    }

    return retList;
}


export const reducer: Reducer<HNIContextState> = (state: HNIContextState | undefined, incomingAction: Action): HNIContextState => {
    if (state === undefined || incomingAction === undefined) {
        return emptyState;
    }

    const action = incomingAction as KnownAction;
    switch (action.type) {

        case "REQUEST_PATIENT_MEDICAL_RECORD_NAME":
            return {
                ...state,
                MedicalRecord: {
                    ...state.MedicalRecord,
                    patientId: action.ptid,
                    recordName: action.type,
                    isLoading: true,
                    isLoaded: false,
                    medicalRecord: undefined
                },
            };
        case "RECEIVE_PATIENT_MEDICAL_RECORD_NAME":
            return {
                ...state,
                MedicalRecord: {
                    ...state.MedicalRecord,
                    medicalRecord: action.subjectMedicalRecord,
                    isLoading: false,
                    isLoaded: true
                },
            }
        case "RECEIVED_FOCUS_MESSAGE": {
            let newFocusMessage = action.Message;
            let tempCurMessages = state.Messages.CurrentMessages;

            return {
                ...state,

                Messages: {
                    ...state.Messages,
                    isLoading: false,
                    CurrentMessages: addMessageIfMissing(newFocusMessage, tempCurMessages),
                    FocusMessage: {
                        isLoading: false,
                        message: newFocusMessage,
                        messageId: newFocusMessage.messageId,
                        isErrored: false,
                        errorMessage: ''
                    }

                }
            }
        };
            break;
        case "REQUEST_PATIENTS_BY_REFMD_ID":
            return {
                ...state,
                Patients: {
                    ...state.Patients,
                    refMdId: action.refMdId,
                    isLoading: true,
                    isLoaded: false,
                    patientsByRefMDId: []
                }
            };
            break;
        case "REQUEST_PATIENTS_BY_PRACTICE_ID":
            return {
                ...state,
                Patients: {
                    ...state.Patients,
                    refMdId: 0,
                    isLoading: true,
                    isLoaded: false,
                    patientsByPracticeId: []
                }
            };
            break;
        case "RECEIVE_PATIENTS_BY_REFMD_ID_ERROR":

            return {
                ...state,
                Patients: {
                    ...state.Patients,
                    patientsByRefMDId: [],
                    isLoading: false,
                    isError: true,
                    errorMessage: action.err
                },
            }
            break;
        case "RECEIVE_PATIENTS_BY_REFMD_ID":
            let patients = action.patientsByRefMDId;
            return {
                ...state,
                Patients: {
                    ...state.Patients,
                    patientsByRefMDId: action.patientsByRefMDId,
                    fullPatients: action.patientsByRefMDId,
                    isLoading: false,
                    isLoaded: true
                }
            }
            break;

        case "RECEIVE_PATIENTS_BY_PRACTICEID":
            let patientsBypracticeId = action.patientsByPracticeId;
            return {
                ...state,
                Patients: {
                    ...state.Patients,
                    patientsByPracticeId: patientsBypracticeId,
                    isLoading: false,
                    isLoaded: true
                }
            }
            break;
        case "REQUEST_PATIENT_BY_ID":
            return {
                ...state,
                FocusPatient: {
                    ...state.FocusPatient,
                    isLoading: true
                }
            }
            break;
        case "RECEIVE_PATIENT_BY_ID":
            let patientsState = state.Patients;
            let fullPatients = [...patientsState.fullPatients, action.patient];


            return {
                ...state,
                Patients: {
                    ...patientsState,
                    fullPatients: fullPatients
                },
                FocusPatient: {
                    ...state.FocusPatient,
                    isLoading: false,
                    patient: action.patient
                }
            }
            break;
        case "REQUEST_USER_REFMD_PROFILE":
            return {
                ...state,
                isLoading: true,
                userRefMdProfile: {
                    isLoading: true,
                    isErrored: false,
                    isLoaded: false,
                    notFound: false,
                    error: ''
                }
            }
            break;

        case "REQUEST_ADMIT_STATS":
            let admitStatsMessage = state.PhysicianAdmitsStats;
            return {
                ...state,
                PhysicianAdmitsStats: {
                    ...admitStatsMessage,
                    isLoading: true,
                    isLoaded: false,
                    physicianAdmits: []
                }
            }
            break;
        case "REQUEST_DISCHARGE_STATS_LOCATIONS":
            let dischargeLocStats = state.DischargeDispositionStats;
            return {
                ...state,
                DischargeDispositionStats: {
                    ...dischargeLocStats,
                    isLoading: true,
                    isLoaded: false,
                    dischargeDispositions: []
                }
            }
            break;
        case "REQUEST_NSOC_TYPE_STATS":
            let dischargeDispState = state.DischargeDispositionStats;
            return {
                ...state,
                DischargeDispositionStats: {
                    ...dischargeDispState,
                    isLoading: true,
                    isLoaded: false,
                    dischargeDispositions: []
                }
            }
            break;
        case "RECEIVE_NSOC_STATS":
            let dischargeLocations = state.DischargeDispositionStats;
            return {
                ...state,
                DischargeDispositionStats: {
                    ...dischargeLocations,
                    isLoading: false,
                    isLoaded: true,
                    isErrored: false,
                    dischargeDispositions: action.dischargeDispositions
                }
            }
            break;
        case "RECEIVE_NSOC_STATS_ERROR":
            let existingState = state.DischargeDispositionStats;
            return {
                ...state,
                DischargeDispositionStats: {
                    ...existingState,
                    isLoading: false,
                    isErrored: true,
                    isLoaded: false,
                    error: action.errMessage,
                    dischargeDispositions: []
                }
            }
            break;
        case "RECEIVE_USER_REFMD_PROFILE_NOT_FOUND":
            let curRefProfileStateVal = state.userRefMdProfile;
            return {
                ...state,
                userRefMdProfile: {
                    ...curRefProfileStateVal,
                    refmdProfile: undefined,
                    isLoaded: false,
                    isErrored: false,
                    isLoading: false,
                    notFound: true
                }
            }
            break;
        case "RECEIVE_USER_REFMD_PROFILE_FAILED":
            let curRefProfileStateValError = state.userRefMdProfile;
            return {
                ...state,
                userRefMdProfile: {
                    ...curRefProfileStateValError,
                    isErrored: true,
                }
            }
            break;
        case "RECEIVE_USER_REFMD_PROFILE": {
            let curRefProfileState = state.userRefMdProfile;
            return {
                ...state,

                userRefMdProfile: {
                    ...curRefProfileState,
                    refmdProfile: action.profile,
                    isLoaded: true,
                    isErrored: false,
                    isLoading: false,
                    notFound: false
                }
            }
        };
            break;
        case "REQUEST_USER_CARE_COORD_PROFILE": {
            let curRefProfileStateRequest = state.userPracticeCoordinatorProfile;

            return {
                ...state,
                userPracticeCoordinatorProfile: {
                    ...curRefProfileStateRequest,
                    isLoaded: false,
                    isErrored: false,
                    isLoading: true,
                    notFound: false
                }
            }
        };
            break;
        case "RECEIVE_USER_CARE_COORD_PROFILE": {
            let curRefProfileState = state.userPracticeCoordinatorProfile;

            return {
                ...state,
                userPracticeCoordinatorProfile: {
                    ...curRefProfileState,
                    coordinatorProfile: action.coordinatorProfile,
                    isLoaded: true,
                    isErrored: false,
                    isLoading: false,
                    notFound: false
                }
            }
        };
            break;
        case "RECEIVE_USER_CARE_COORD_PROFILE_NOT_FOUND": {
            let curRefProfileState = state.userPracticeCoordinatorProfile;

            return {
                ...state,
                userPracticeCoordinatorProfile: {
                    ...curRefProfileState,
                    coordinatorProfile: undefined,
                    isLoaded: false,
                    isErrored: false,
                    isLoading: false,
                    notFound: true
                }
            }
        }
            break;

        case "RECEIVE_REF_MD_ADMIT_STATS": {
            let admitStatsMessage = state.PhysicianAdmitsStats;
            return {
                ...state,
                PhysicianAdmitsStats: {
                    ...admitStatsMessage,
                    isLoading: false,
                    isLoaded: true,
                    physicianAdmits: action.admitStats
                }
            }
        };
            break;
        case "REQUEST_ADMIT_TYPE_STATS": {
            return {
                ...state,
                PhysicianTypeStats: {
                    ...state.PhysicianTypeStats,
                    isLoading: true,
                    isLoaded: false,
                    admitTypeStats: []
                }
            }
        };
            break;
        case "RECEIVE_ADMIT_TYPE_STATS": {
            return {
                ...state,
                PhysicianTypeStats: {
                    ...state.PhysicianTypeStats,
                    isLoading: false,
                    isLoaded: true,
                    isErrored: false,
                    admitTypeStats: action.admitTypeStats
                }
            }
        };
            break;
        case "REQUEST_ADMIT_TYPE_STATS_ERROR": {
            return {
                ...state,
                PhysicianTypeStats: {
                    ...state.PhysicianTypeStats,
                    isLoading: false,
                    isLoaded: false,
                    isErrored: true,
                    error: action.errMessage,
                    admitTypeStats: []
                }
            }
        };
            break;
        case "REQUEST_PATIENT_STATS_LOCATIONS": {
            let patientLocationStatsState = state.PatientLocationStats;
            return {
                ...state,
                PatientLocationStats: {
                    ...patientLocationStatsState,
                    isLoading: true,
                    isLoaded: false,
                    patientFacilityCounts: []
                }
            }
        }
            break;
        case "REQUEST_ADMIT_STATS_ERROR": {
            let admitStatsMessage = state.PhysicianAdmitsStats;
            return {
                ...state,
                PhysicianAdmitsStats: {
                    ...admitStatsMessage,
                    isLoading: false,
                    isErrored: true,
                    isLoaded: false,
                    physicianAdmits: []
                }
            }
        }
            break;

        case "REQUEST_ADMIT_MONTH_STATS": {
            let admitMonthStatsMessage = state.PhysicianAdmitMonthStats;
            return {
                ...state,
                PhysicianAdmitMonthStats: {
                    ...admitMonthStatsMessage,
                    isLoading: true,
                    isLoaded: false,
                    physicianAdmits: []
                }
            }
        }
            break;
        case "RECEIVE_ADMIT_MONTH_STATS": {
            let admitMonthStatsMessage = state.PhysicianAdmitMonthStats;
            return {
                ...state,
                PhysicianAdmitMonthStats: {
                    ...admitMonthStatsMessage,
                    isLoading: false,
                    isLoaded: true,
                    physicianAdmits: action.admitStats
                }
            }
        };
            break;
        case "REQUEST_ADMIT_MONTH_STATS_ERROR": {
            let admitMonthStatsMessage = state.PhysicianAdmitMonthStats;
            return {
                ...state,
                PhysicianAdmitMonthStats: {
                    ...admitMonthStatsMessage,
                    isLoading: false,
                    isErrored: true,
                    isLoaded: false,
                    physicianAdmits: []
                }
            }
        }
            break;

        case "RECEIVE_DISCHARGE_STATS_LOCATIONS_ERROR": {
            let dischargeLocStats = state.DischargeLocationStats;
            return {
                ...state,
                DischargeLocationStats: {
                    ...dischargeLocStats,
                    isLoading: false,
                    isErrored: true,
                    dischargeLocations: []
                }
            }
        }
            break;
        case "RECEIVE_DISCHARGE_STATS_LOCATIONS": {
            let dischargeLocStats = state.DischargeLocationStats;
            return {
                ...state,
                DischargeLocationStats: {
                    ...dischargeLocStats,
                    isLoading: false,
                    dischargeLocations: action.dischargeLocations
                }
            }
        }
            break;
        case "REQUEST_PATIENTS_LOCATION_STATS": {
            let patientLocStats = state.PatientLocationStats;
            return {
                ...state,
                PatientLocationStats: {
                    ...patientLocStats,
                    isLoading: true,
                    isLoaded: false,
                    isErrored: false
                }
            }
        }
        case "RECEIVE_PATIENTS_LOCATION_STATS": {
            let patientLocStats = state.PatientLocationStats;
            return {
                ...state,
                PatientLocationStats: {
                    ...patientLocStats,
                    isLoading: false,
                    isLoaded: true,
                    patientFacilityCounts: action.patientFacilityCounts
                }
            }
        }
            break;
        case "REQUEST_PATIENT_LOCATION_DRILLDOWN_STATS": {
            let drillDownStats = state.PatientFacilityDrillDownStats;
            return {
                ...state,
                PatientFacilityDrillDownStats: {
                    ...drillDownStats,
                    isLoading: true,
                    isLoaded: false,
                    isErrored: false,
                    patientFacilityDrillDownCounts: null
                }
            }
        }
            break;
        case "RECEIVE_PATIENT_LOCATION_DRILLDOWN_STATS": {
            let drillDownStats = state.PatientFacilityDrillDownStats;
            return {
                ...state,
                PatientFacilityDrillDownStats: {
                    ...drillDownStats,
                    isLoading: false,
                    isLoaded: true,
                    patientFacilityDrillDownCounts: action.patientFacilityDrillDownCounts
                }
            }
        }
            break;
        case "RECEIVE_PATIENT_LOCATION_DRILLDOWN_STATS_ERROR": {
            let drillDownStats = state.PatientFacilityDrillDownStats;
            return {
                ...state,
                PatientFacilityDrillDownStats: {
                    ...drillDownStats,
                    isLoading: false,
                    isLoaded: false,
                    isErrored: true,
                    error: action.errMessage,
                    patientFacilityDrillDownCounts: null
                }
            }
        }
            break;
        case "RECEIVE_ALL_SYSTEM_SPECIALTIES": {
            let availSpecialties = action.AvailableSpecialties;
            return {
                ...state,
                SystemSpecialties: {
                    isLoading: false,
                    isLoaded: true,
                    VitalsReferenceSpecialties: availSpecialties
                }
            }

        };
            break;
        case "REQUEST_AVAILABLE_SPECIALTIES": {

            let requestState = {
                ...state.SystemSpecialties,
                isLoading: true,
                isLoaded: false
            };
            return {
                ...state,
                SystemSpecialties: requestState
            }
        };
            break;
        case "REQUEST_USER_PRACTICE_PHYSICIAN_CONTACTS": {
            return {
                ...state,
                PatientPhysiciansContacts: {
                    isErrored: false,
                    isLoaded: false,
                    isLoading: true,
                    error: '',
                    userPatientContacts: []
                }
            }
        };
            break;
        case "RECEIVE_USER_PRACTICE_PHYSICIAN_CONTACTS": {
            let userContacts = filterServiceDeskContacts(action.physicianContacts)
            return {
                ...state,
                PatientPhysiciansContacts: {
                    isErrored: false,
                    isLoaded: true,
                    isLoading: false,
                    error: '',
                    userPatientContacts: userContacts
                }
            }
        };
            break;
        case "REQUEST_CURRENT_MESSAGES": {
            let sendState = state.Messages.SendMessageState;
            let requestState = {
                ...state.Messages,
                isLoading: true,
                isLoaded: false
            };
            return {
                ...state,
                Messages: requestState
            }
        }
            break;
        case "RECEIVE_CURRENT_MESSAGES": {
            let messages = action.Messages;
            return {
                ...state,

                Messages: {
                    ...state.Messages,
                    isLoaded: true,
                    isLoading: false,
                    CurrentMessages: messages,
                }
            }
        };
            break;

        case "REQUEST_FOCUS_MESSAGE_PARTIAL": {
            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    isLoaded: false,
                    FocusMessage: {
                        ...state.Messages.FocusMessage,
                        isLoading: true,
                        errorMessage: '',
                        isErrored: false
                    }
                }
            }
        }
            break;
        case "REQUEST_FOCUS_MESSAGE": {
            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    FocusMessage: {
                        ...state.Messages.FocusMessage,
                        isLoading: true,
                        errorMessage: '',
                        isErrored: false
                    }
                }
            }
        }
            break;
        case "RECEIVE_FOCUS_MESSAGE_ERROR": {
            return {
                ...state,

                Messages: {
                    ...state.Messages,
                    FocusMessage: {
                        ...state.Messages.FocusMessage,
                        isLoading: false,
                        errorMessage: action.errorMessage,
                        isErrored: true
                    }
                }
            }
        }
            break;

        case "ARCHIVED_MESSAGE_RESPONSE": {
            let message = action.archived;
            let newMessagesState = removeMessage(message, state.Messages.CurrentMessages)
            return {
                ...state,

                Messages: {
                    ...state.Messages,
                    isLoaded: true,
                    isLoading: false,
                    CurrentMessages: newMessagesState
                }
            }
        };
            break;
        case "ARCHIVED_MESSAGES_RESPONSE": {
            let removedMessages = action.archived;
            let newMessagesState = removeMessages(removedMessages, state.Messages.CurrentMessages);
            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    isLoaded: true,
                    isLoading: false,
                    CurrentMessages: newMessagesState,

                }
            }
        };
            break;
        case "RECEIVED_MESSAGE_REPLY_RESPONSE": {
            let newPersistedMessageReply = action.message;
            let currentMessages = [...state.Messages.CurrentMessages, newPersistedMessageReply];

            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    isLoaded: true,
                    isLoading: false,
                    CurrentMessages: currentMessages,
                    SendMessageState: {
                        message: newPersistedMessageReply,
                        isSent: true,
                        isErrored: false,
                        error: ''
                    }
                }
            }
        };
            break;
        case "CLEAR_SEND_MESSAGE_RESPONSE": {
            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    Errors: [],
                    SendMessageState: {
                        message: null,
                        isSent: false,
                        isErrored: false,
                        error: ''
                    }
                }
            }
        }
            break;
        case "RECEIVED_NEW_MESSAGE_SEND_RESPONSE": {
            let newPersistedMessage = action.message;
            let currentMessagesNewItem = [...state.Messages.CurrentMessages, newPersistedMessage];
            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    CurrentMessages: currentMessagesNewItem,
                    isLoaded: true,
                    isLoading: false,
                    failedLoading: false,

                    SendMessageState: {
                        message: action.message,
                        isSent: true,
                        isErrored: false,
                        error: ''
                    }
                }
            }
        }
            break;
        case "RECEIVED_NEW_MESSAGE_SEND_RESPONSE_ERROR": {
            let sendMessageState = state.Messages.SendMessageState;
            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    isLoaded: true,
                    isLoading: false,

                    SendMessageState: {
                        ...sendMessageState,
                        isSent: false,
                        isErrored: true,
                        error: action.ErrorMessage
                    }
                }
            }
        }
            break;
        case "RECEIVE_CURRENT_MESSAGES_ERROR": {
            let newError: MessageStateError = {
                messageId: -1,
                error: action.ErrorMessage
            }
            let curErrors = [...state.Messages.Errors, newError]
            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    isLoaded: false,
                    isLoading: false,
                    failedLoading: true,
                    Errors: curErrors
                }
            }
        }
            break;
        case "ARCHIVE_MESSAGE_ERROR": {
            let id = action.messageId;
            let err = action.errMessage;


            let newError: MessageStateError = {
                messageId: id,
                error: err
            }

            let newErrors = [...state.Messages.Errors, newError];

            return {
                ...state,
                Messages: {
                    ...state.Messages,
                    isLoaded: true,
                    isLoading: false,
                    Errors: newErrors
                }
            }
        }; break;

        case "RECEIVE_NOTIFICATION_UNREAD_COUNT_ERROR": {
            return {
                ...state,
                NotificationCount: {
                    ...state.NotificationCount,
                    isLoaded: false,
                    isErrored: true,
                    isLoading: false,
                    UnreadCount: 0

                }
            }
        } break;

        case "RECEIVE_NOTIFICATION_UNREAD_COUNT": {
            return {
                ...state,
                NotificationCount: {
                    ...state.NotificationCount,
                    isLoaded: true,
                    isErrored: false,
                    isLoading: false,
                    UnreadCount: action.countStatus.unreadCount

                }
            }
        }
            break;
        case "REQUEST_NOTIFICATION_UNREAD_COUNT": {
            return {
                ...state,
                NotificationCount: {
                    ...state.NotificationCount,
                    isLoaded: false,
                    isLoading: true,
                    UnreadCount: 0
                }
            }
        }
            break;
        case "UPDATE_API_HOST": {
            return {
                ...state,
                apiHost: action.apiHost
            }
        }
            break;
        case "UPDATE_PATIENT_LIST_PARAMS": {
            debugger
            return {
                ...state,
                PatientListParams: {
                    ...state.PatientListParams,
                    daysBack: action.patientListParams.daysBack,
                    columnFilterMap: action.patientListParams.columnFilterMap
                }
            }
        }
            break;
        case "UPDATE_MESSAGE_PARAMS": {
            return {
                ...state,
                MessageParams: {
                    ...state.MessageParams,
                    daysBack: action.messageParams.daysBack
                }
            }
        }
            break;
        case "RECEIVE_TOKEN_RESPONSE": {
            return {
                ...state,
                APIToken: action.apiToken
            }
        }
    }

    return state;
};

